import './i18n/autoTranslate';
import { useI18nText } from './i18n/useI18n';
import LanguageSwitcher from './i18n/LanguageSwitcher';
import React, { useState, useEffect, useRef } from 'react';
import { 
  ArrowRight, Activity, Bot, Video, Mic, BarChart3, 
  CheckCircle, Sparkles, Cpu, Shield,
  BookOpen, Users, FileText, Check, 
  Star, ChevronDown, ChevronUp, Play,
  MessageSquare, Eye, Zap, Target,
  Calendar, Clock, Award, MicOff, Volume2,
  UserCheck, Lightbulb, TrendingUp,
  Settings, User, Download, MessageCircle,
  Layout, Minus, RefreshCw, Unlock, Map, Phone, 
  Building, UserPlus, BarChart, GraduationCap, Globe,Blocks,
  TableOfContents,
  GitCompareArrows,
  
} from 'lucide-react';
import '../styles/index.css';
import { motion, useScroll, useTransform, AnimatePresence, useInView } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { FaQuestion } from "react-icons/fa6";
// Import your components
import TextType from '../components/ui/TextType';
import BlurText from '../components/ui/BlurText';
import Silk from '../components/ui/Silk';
import AppBackground from '../components/ui/AppBackground.tsx';
import Threads from '../components/ui/Threads';
import CardSwap, { Card } from '../components/ui/CardSwap';
import LogoLoop from '../components/ui/LogoLoop';
import DemoPreviewModal from '../pages/demo/DemoPreviewModal';

import { FaGoogle } from "react-icons/fa";
import { BsAmazon } from "react-icons/bs";
import { TfiMicrosoftAlt } from "react-icons/tfi";
import { BsMeta } from "react-icons/bs";
import { SiNetflix } from "react-icons/si";
import LightRays from '../components/ui/LightRays';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip"; 
import { Plus } from 'lucide-react';

import { BentoGrid, BentoGridItem } from "../components/ui/bento-grid";
import { AnimatedTestimonials } from "../components/ui/animated-testimonials";
import { TeamCarousel } from '../components/lightswind/team-carousel';
import SEO from './SEO';

// Animation Components
const SectionWrapper = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const StaggerContainer = ({ children, className = "" }) => {
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
      variants={{
        hidden: { opacity: 0 },
        visible: {
          opacity: 1,
          transition: {
            staggerChildren: 0.1,
            delayChildren: 0.2
          }
        }
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const StaggerItem = ({ children, index = 0, className = "" }) => {
  const direction = index % 2 === 0 ? -100 : 100;
  
  return (
    <motion.div
      initial={{ opacity: 0, x: direction, y: 100 }}
      whileInView={{opacity: 1, x: 0, y: 0}}
      transition= {{
        duration: 2,
        ease: [0.22, 1, 0.36, 1]
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const GlassCard = ({ src="",children, className = "", hover = true }) => {
  const cardRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  const handleMouseMove = (e) => {
    const rect = cardRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 10;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * 10;
    setMousePosition({ x, y });
  };
  
  const handleMouseLeave = () => {
    setMousePosition({ x: 0, y: 0 });
  };
  
  return (
    <motion.div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      whileHover={hover ? { 
        y: -8, 
        transition: { duration: 0.3 } 
      } : {}}
      style={{
        transform: `perspective(1000px) rotateX(${mousePosition.y}deg) rotateY(${-mousePosition.x}deg)`,
        transition: 'transform 0.3s ease-out'
      }}
      className={`
        group relative overflow-hidden
        rounded-3xl border border-white/10
        bg-white/[0.035] backdrop-blur-2xl
        ring-1 ring-inset ring-white/10
        shadow-[0_18px_55px_-30px_rgba(0,0,0,0.85),0_0_90px_rgba(108,99,255,0.10)]
        ${hover ? 'hover:bg-white/[0.055] hover:border-white/20 hover:shadow-[0_22px_70px_-34px_rgba(0,0,0,0.90),0_0_120px_rgba(108,99,255,0.16)]' : ''}
        transition-all duration-300
        ${className}
      `}
    >
      <img src={"/assets/images/Gemini_Generated_6_dlmovh.png"} className="absolute inset-0 -z-30 h-full w-full object-cover rounded-3xl opacity-[0.08]" alt="" />
      <div className="pointer-events-none absolute inset-0 -z-20 rounded-3xl bg-[radial-gradient(900px_260px_at_20%_0%,rgba(108,99,255,0.28),transparent_60%)] opacity-80" />
      <div className="pointer-events-none absolute inset-0 -z-20 rounded-3xl bg-[radial-gradient(800px_240px_at_80%_30%,rgba(59,130,246,0.18),transparent_65%)] opacity-80" />
      <div className="pointer-events-none absolute inset-0 -z-10 rounded-3xl bg-gradient-to-b from-white/[0.06] via-transparent to-black/40" />
      {children}
    </motion.div>
  );
};

const GlassmorphicLandingPage = () => {
  const { language: i18nLanguage, t, list: tList } = useI18nText();
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [demoScore, setDemoScore] = useState(0);
  const navigate = useNavigate();

  const SIGNUP_ROUTE = "/sign-up";
  const FREE_INTERVIEW_ALLOWANCE = 2;

  const handleSignup = (source = "landing_cta") => {
    localStorage.setItem("intendedPlan", "free");
    localStorage.setItem("signupSource", source);
    localStorage.setItem("freePlanAllowance", String(FREE_INTERVIEW_ALLOWANCE));

    navigate(SIGNUP_ROUTE, {
      state: {
        plan: "free",
        interviewsPerMonth: FREE_INTERVIEW_ALLOWANCE,
        source,
      },
    });
  };
  
  const [demoState, setDemoState] = useState('idle');
  const [selectedRole, setSelectedRole] = useState('software-engineer');
  
  const handleStartDemo = () => {
    setDemoState('listening');
  };
  
  const handleRespond = () => {
    setDemoState('analyzing');
    setTimeout(() => {
      let score = 0;
      const interval = setInterval(() => {
        score += 2;
        setDemoScore(score);
        if (score >= 82) {
          clearInterval(interval);
          setDemoState('results');
        }
      }, 30);
    }, 2000);
  };
  
  const handleRetry = () => {
    setDemoState('idle');
    setDemoScore(0);
  };

  const SignupTrustHooks = ({ compact = false }) => (
    <div className={`${compact ? "mt-5" : "mt-7"} grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl ${compact ? "mx-auto" : "mx-auto lg:mx-0"}`}>
      {[
        { icon: <UserPlus size={16} />, title: 'Free account', text: 'Sign up in seconds' },
        { icon: <CheckCircle size={16} />, title: `${FREE_INTERVIEW_ALLOWANCE} AI interviews`, text: 'Included every month' },
        { icon: <Shield size={16} />, title: 'No credit card', text: 'Start free. Upgrade only when ready' },
      ].map((item) => (
        <div key={item.title} className="rounded-2xl border border-white/10 bg-white/[0.035] px-4 py-3 backdrop-blur-xl text-left">
          <div className="flex items-center gap-2 text-white font-semibold text-sm mb-1">
            <span className="text-[#8B7CFF]">{item.icon}</span>
            {item.title}
          </div>
          <p className="text-xs text-white/55 leading-relaxed">{item.text}</p>
        </div>
      ))}
    </div>
  );

  // Demo State Components
  const IdleState = ({ onStartDemo }) => (
    <>
      <div className="flex items-center gap-4 mb-8">
        <div className="relative">
          <div className="w-14 h-14 flex items-center justify-center">
            <img src={"/GenieAI_2.png"} className="text-white" />
          </div>
          <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-400 rounded-full border-2 border-[#07070A]"></div>
        </div>
        <div>
          <p className="font-semibold text-white text-lg">InterviewGenie AI</p>
          <p className="text-sm text-gray-400">Ready to analyze your interview skills</p>
        </div>
      </div>
      
      <div className="mb-8">
        <label className="block text-gray-300 text-sm font-medium mb-3">
          Choose Your Target Role:
        </label>
        <select 
          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#6C63FF] focus:border-transparent backdrop-blur-sm"
          onChange={(e) => setSelectedRole(e.target.value)}
          value={selectedRole}
        >
          <option className='text-gray-900' value="software-engineer">Software Engineer</option>
          <option className='text-gray-900' value="product-manager">Product Manager</option>
          <option className='text-gray-900' value="marketing-manager">Marketing Manager</option>
          <option className='text-gray-900' value="data-scientist">Data Scientist</option>
          <option className='text-gray-900' value="ux-designer">UX Designer</option>
        </select>
      </div>
      
      <GlassCard className="p-6 mb-6">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-[#6C63FF]/20 flex items-center justify-center flex-shrink-0 mt-1">
            <MessageCircle size={16} className="text-[#6C63FF]" />
          </div>
          <div>
            <p className="text-white font-medium mb-2">Demo Question:</p>
            <p className="text-gray-200 text-lg leading-relaxed">
              "Tell me about yourself and why you're interested in this {selectedRole.replace('-', ' ')} position."
            </p>
          </div>
        </div>
      </GlassCard>
      
      <motion.button 
        whileHover={{ scale: 1.05 }}
        onClick={onStartDemo}
        
        whileTap={{ scale: 0.95 }}
        style={{
          boxShadow: 'inset 0 -4px 10px rgba(255, 255, 255, 0.3), 0 0 40px rgba(108, 99, 255, 0.3)'
        }}
        className="w-full px-3 py-2 group rounded-xl bg-gradient-to-br from-[#8B7CFF] to-[#6C63FF] text-white font-medium transition-all duration-300 hover:shadow-[0_0_50px_rgba(108,99,255,0.4)] flex items-center justify-center gap-2 relative overflow-hidden"
      >
        <div 
          className="absolute -bottom-1 left-[20%] w-1/2 right-0 h-4 rounded-full bg-white/70 blur-[10px]  pointer-events-none"
        />
        <Zap size={20} className="group-hover:scale-110 transition-transform" />
        <span className="relative z-10">Start Demo Challenge</span>
        <ArrowRight className="group-hover:animate-pulse group-hover:translate-x-1 transition-transform" size={20} />
      </motion.button>

    </>
  );
  
  const ListeningState = ({ onRespond }) => (
    <>
      <div className="flex items-center gap-4 mb-8">
        <div className="relative">
          <div className="relative">
            <div className="w-14 h-14 flex items-center justify-center">
              <img src={"/GenieAI_2.png"} className="text-white" />
            </div>
            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-400 rounded-full border-2 border-[#07070A]"></div>
          </div>
          <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-yellow-400 rounded-full border-2 border-[#07070A] animate-pulse"></div>
        </div>
        <div>
          <p className="font-semibold text-white text-lg">InterviewGenie AI</p>
          <p className="text-sm text-gray-400">Listening to your response...</p>
        </div>
      </div>
      
      <div className="flex justify-center mb-8">
        <div className="flex items-center gap-8">
          {['Listen', 'Respond', 'Score'].map((step, index) => (
            <div key={step} className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${
                index === 0 
                  ? 'bg-[#6C63FF] text-white shadow-lg shadow-[#6C63FF]/25' 
                  : 'bg-white/10 text-gray-400'
              }`}>
                {index + 1}
              </div>
              <span className={`text-sm font-medium ${
                index === 0 ? 'text-[#6C63FF]' : 'text-gray-500'
              }`}>
                {step}
              </span>
              {index < 2 && (
                <div className="w-8 h-0.5 bg-white/10"></div>
              )}
            </div>
          ))}
        </div>
      </div>
      
      <GlassCard className="p-6 mb-6">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-[#6C63FF]/20 flex items-center justify-center flex-shrink-0 mt-1">
            <MessageCircle size={16} className="text-[#6C63FF]" />
          </div>
          <div>
            <p className="text-white font-medium mb-2">Your Response:</p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-red-500/20 border border-red-500/30 rounded-lg text-red-300">
                <Mic size={16} />
                Recording...
              </div>
              <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-red-500 to-pink-500 rounded-full animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>
      </GlassCard>
      <motion.button 
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={onRespond}
        style={{
          boxShadow: 'inset 0 -4px 10px rgba(255, 255, 255, 0.3), 0 0 40px rgba(108, 99, 255, 0.3)'
        }}
        className="w-full px-3 py-2 group rounded-xl bg-gradient-to-br from-[#70cc9a] to-[#14e1c2] text-white font-medium transition-all duration-300 hover:shadow-[0_0_50px_rgba(108,99,255,0.4)] flex items-center justify-center gap-2 relative overflow-hidden"
      >
        <div 
          className="absolute -bottom-1 left-[20%] w-1/2 right-0 h-4 rounded-full bg-white/70 blur-[10px]  pointer-events-none"
        />
        <span className="relative z-10">Get Started</span>
        <ArrowRight className="group-hover:animate-pulse group-hover:translate-x-1 transition-transform" size={18} />
      </motion.button>
      {/* <motion.button 
        onClick={onRespond}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-6 py-4 rounded-xl font-semibold text-lg transition-all duration-300 shadow-[0_0_40px_rgba(72,187,120,0.3)] flex items-center justify-center gap-3"
      >
        <Check size={20} />
        Submit & Analyze Response
      </motion.button> */}
    </>
  );
  
  const AnalyzingState = () => (
    <div className="text-center py-12 relative">
      {/* Background glow effect */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-gradient-to-br from-[#6C63FF]/10 to-transparent rounded-full blur-3xl opacity-60" />
      
      {/* Animated loading container */}
      <div className="relative w-32 h-32 mx-auto mb-8">
        {/* Outer platinum ring */}
        <div className="absolute inset-0 border-2 border-white/20 rounded-full shadow-[0_0_30px_rgba(255,255,255,0.1)]"></div>
        
        {/* Pulsing gradient rings */}
        <div className="absolute inset-2 border-2 border-transparent rounded-full animate-spin">
          <div className="absolute -inset-1 bg-gradient-to-r from-[#6C63FF] via-[#8B7CFF] to-[#6C63FF] rounded-full blur-sm opacity-30" />
        </div>
        
        {/* Main rotating ring with platinum shine */}
        <div className="absolute inset-4 border-[3px] border-transparent rounded-full animate-spin animation-delay-300">
          <div className="absolute -inset-1 bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent rounded-full blur-[2px] opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#6C63FF] to-[#8B7CFF] rounded-full" />
          <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-transparent to-transparent rounded-full" />
        </div>
        
        {/* Inner counter-rotating ring */}
        <div className="absolute inset-8 border-2 border-transparent rounded-full animate-spin-reverse animation-delay-100">
          <div className="absolute -inset-1 bg-gradient-to-r from-[#8B7CFF] to-[#6C63FF] rounded-full blur-[1px] opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#8B7CFF] to-[#6C63FF] rounded-full" />
        </div>
        
        {/* Central AI icon with glow */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-[#6C63FF] to-transparent rounded-full blur-md opacity-20 animate-pulse" />
            <div className="relative w-16 h-16 rounded-2xl border border-white/20 bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl flex items-center justify-center">
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent rounded-2xl" />
              <Bot size={28} className="text-[#6C63FF] relative z-10" />
            </div>
          </div>
        </div>
        
        {/* Floating particles */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1 w-2 h-2 rounded-full bg-[#6C63FF] animate-bounce opacity-70" />
        <div className="absolute bottom-0 right-1/2 translate-x-1/2 translate-y-1 w-1.5 h-1.5 rounded-full bg-[#8B7CFF] animate-bounce animation-delay-300 opacity-60" />
        <div className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-1 w-1 h-1 rounded-full bg-[#6C63FF] animate-bounce animation-delay-600 opacity-50" />
      </div>
      
      {/* Title with platinum gradient */}
      <h3 className="text-2xl font-bold bg-gradient-to-r from-white via-white/90 to-white bg-clip-text text-transparent mb-3 relative">
        AI Analysis in Progress
        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-16 h-px bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent"></div>
      </h3>
      
      <p className="text-gray-400 mb-10 max-w-md mx-auto leading-relaxed">
        Our AI is meticulously evaluating every aspect of your response...
      </p>
      
      {/* Analysis categories with platinum cards */}
      <div className="flex justify-center gap-8 max-w-2xl mx-auto">
        {['Structure', 'Clarity', 'Confidence', 'Filler Words'].map((category, index) => (
          <motion.div 
            key={category} 
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <div className="relative group">
              {/* Card glow */}
              <div className="absolute -inset-0.5 bg-gradient-to-br from-[#6C63FF]/30 to-transparent rounded-2xl blur-sm opacity-0 group-hover:opacity-50 transition-opacity duration-300" />
              
              {/* Category card */}
              <div className="relative w-16 h-16 rounded-2xl border border-white/15 bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-lg flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300">
                {/* Inner shine */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent rounded-2xl" />
                
                {/* Pulsing indicator */}
                <div className="relative">
                  <div className="absolute -inset-2 bg-gradient-to-br from-[#6C63FF] to-transparent rounded-full blur-sm animate-ping opacity-20" />
                  <div className="w-3 h-3 rounded-full bg-gradient-to-br from-[#6C63FF] to-[#8B7CFF] shadow-[0_0_10px_rgba(108,99,255,0.5)]"></div>
                </div>
              </div>
              
              {/* Category label */}
              <span className="text-sm font-medium text-gray-300 group-hover:text-white transition-colors block">
                {category}
              </span>
              
              {/* Loading dots animation */}
              <div className="flex justify-center gap-1 mt-2 opacity-70">
                {[0, 1, 2].map((dot) => (
                  <motion.div
                    key={dot}
                    className="w-1 h-1 rounded-full bg-[#6C63FF]"
                    animate={{ opacity: [0.3, 1, 0.3] }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      delay: dot * 0.2,
                    }}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      
      {/* Progress indicator text */}
      <motion.p 
        className="text-xs text-gray-500 mt-8 font-medium tracking-wider uppercase"
        animate={{ opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        • Processing speech patterns • Analyzing tone • Evaluating structure •
      </motion.p>
    </div>
  );
  
  const CircularProgress = ({ score }) => {
    const radius = 60;
    const circumference = 2 * Math.PI * radius;
    const strokeDasharray = circumference;
    const strokeDashoffset = circumference - (score / 100) * circumference;

    return (
      <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 140 140">
        <circle
          cx="70"
          cy="70"
          r={radius}
          stroke="rgba(255,255,255,0.1)"
          strokeWidth="8"
          fill="none"
        />
        <circle
          cx="70"
          cy="70"
          r={radius}
          stroke="url(#gradient)"
          strokeWidth="8"
          fill="none"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className="transition-all duration-1000 ease-out"
        />
        <defs>
          <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#6C63FF" />
            <stop offset="100%" stopColor="#8B7CFF" />
          </linearGradient>
        </defs>
      </svg>
    );
  };
  
  const ResultsState = ({ onRetry }) => (
    <>
      <div className="text-center mb-8">
        <div className="w-32 h-32 mx-auto mb-6 relative">
          <div className="absolute inset-0">
            <CircularProgress score={demoScore} />
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-3xl font-bold text-white">{demoScore}%</span>
          </div>
        </div>
        
        <h3 className="text-2xl font-bold text-white mb-2">Great Job!</h3>
        <p className="text-gray-400 mb-4">You're leveling up already</p>
        
        <div className="flex items-center justify-center gap-2 text-[#6C63FF] font-semibold mb-6">
          <TrendingUp size={20} />
          +15 Interview XP
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-8">
        {[
          { name: 'Structure', score: 85, icon: <Layout size={16} /> },
          { name: 'Clarity', score: 78, icon: <MessageSquare size={16} /> },
          { name: 'Confidence', score: 92, icon: <Award size={16} /> },
          { name: 'Filler Usage', score: 65, icon: <Minus size={16} /> }
        ].map((category) => (
          <GlassCard key={category.name} className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#6C63FF]/20 flex items-center justify-center">
                  {category.icon}
                </div>
                <span className="text-white font-medium text-sm">{category.name}</span>
              </div>
              <span className={`text-sm font-bold ${
                category.score >= 80 ? 'text-green-400' : 
                category.score >= 70 ? 'text-yellow-400' : 'text-red-400'
              }`}>
                {category.score}%
              </span>
            </div>
            <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all duration-1000 ${
                  category.score >= 80 ? 'bg-gradient-to-r from-green-500 to-emerald-500' :
                  category.score >= 70 ? 'bg-gradient-to-r from-yellow-500 to-amber-500' :
                  'bg-gradient-to-r from-red-500 to-pink-500'
                }`}
                style={{ width: `${category.score}%` }}
              ></div>
            </div>
          </GlassCard>
        ))}
      </div>
      
      <GlassCard className="p-6 mb-6 border-[#6C63FF]/20 bg-[#6C63FF]/5">
        <h4 className="font-bold text-[#6C63FF] mb-3 flex items-center gap-2">
          <Sparkles size={18} />
          Your Strengths & One Thing to Improve
        </h4>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-300 font-medium mb-2">✅ Strong Areas:</p>
            <ul className="text-gray-400 space-y-1">
              <li>• Clear communication style</li>
              <li>• Good eye contact</li>
              <li>• Professional tone</li>
            </ul>
          </div>
          <div>
            <p className="text-yellow-400 font-medium mb-2">🎯 Focus Area:</p>
            <ul className="text-gray-400 space-y-1">
              <li>• Reduce filler words ("um", "like")</li>
              <li>• Use more role-specific keywords</li>
              <li>• Strengthen opening statement</li>
            </ul>
          </div>
        </div>
      </GlassCard>
      
      <div className="flex gap-4">
        <motion.button 
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onRetry}
          style={{
            boxShadow: 'inset 0 -4px 10px rgba(255, 255, 255, 0.3), 0 0 40px rgba(108, 99, 255, 0.3)'
          }}
          className="flex-1  px-3 py-2 group rounded-xl bg-gradient-to-br from-[#8B7CFF] to-[#6C63FF] text-white font-medium transition-all duration-300 hover:shadow-[0_0_50px_rgba(108,99,255,0.4)] flex items-center justify-center gap-2 relative overflow-hidden"
        >
          <div 
            className="absolute -bottom-1 left-[20%] w-1/2 right-0 h-4 rounded-full bg-white/70 blur-[10px]  pointer-events-none"
          />
          <RefreshCw className="group-hover:animate-pulse group-hover:translate-x-1 transition-transform" size={18} />
          <span className="relative z-10">Try Again</span>
        </motion.button>
        <motion.button 
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => handleSignup("landing_cta")}
          style={{
            boxShadow: 'inset 0 -4px 10px rgba(255, 255, 255, 0.3), 0 0 40px rgba(108, 99, 255, 0.3)'
          }}
          className="flex-1  px-3 py-2 group rounded-xl bg-gradient-to-br from-green-600 to-emerald-600 text-white font-medium transition-all duration-300 hover:shadow-[0_0_50px_rgba(108,99,255,0.4)] flex items-center justify-center gap-2 relative overflow-hidden"
        >
          <div 
            className="absolute -bottom-1 left-[20%] w-1/2 right-0 h-4 rounded-full bg-white/70 blur-[10px]  pointer-events-none"
          />
          <Unlock className="group-hover:animate-pulse group-hover:translate-x-1 transition-transform" size={18} />
          <span className="relative z-10">Create Free Account</span>
        </motion.button>

      </div>
      
      <p className="text-center text-gray-500 text-sm mt-4">
        Create a free account to save your progress and use 2 AI interviews every month
      </p>
    </>
  );

  // FAQ Items
  const faqItems = [
    {
      question: "What makes InterviewGenieAI different from normal mock interview tools?",
      answer: "InterviewGenieAI does not just give you random questions. It simulates realistic AI interviews, analyzes your answers, and tells you exactly what to improve: structure, clarity, confidence, filler words, relevance, and impact."
    },
    {
      question: "Can I practice for my exact role or industry?",
      answer: "Yes. You can train for different roles, industries, seniority levels, and interview formats including behavioral, technical, phone, chat, and panel-style interviews."
    },
    {
      question: "What does the free plan include?",
      answer: "The free plan includes 2 AI interviews every month, basic feedback, and access to the core practice experience. No credit card required."
    },
    {
      question: "Will the AI help me improve weak answers?",
      answer: "Yes. The platform highlights weak areas and gives clear improvement suggestions, including how to make answers more structured, specific, confident, and aligned with the target role."
    },
    {
      question: "Does it support technical interviews?",
      answer: "Yes. InterviewGenieAI supports technical practice flows for developers and other roles, including role-specific questions and feedback focused on explanation quality, problem-solving, and communication."
    },
    {
      question: "Can schools or training centers use InterviewGenieAI?",
      answer: "Yes. Training centers, schools, and career programs can use InterviewGenieAI to help learners practice interviews, track improvement, and prepare for job placement."
    },
    {
      question: "Is my interview data private?",
      answer: "Interview preparation can be personal, so the product should clearly communicate privacy, account control, and data handling. Keep your privacy policy visible and let users know how their data is used."
    },
    {
      question: "Can I cancel anytime?",
      answer: "Yes. Paid users should be able to manage or cancel their plan from the billing area, while keeping access until the end of the active billing period."
    }
  ];

  // Tech Logos
  const techLogos = [
    { node: <FaGoogle />, title: "Google", href: "https://www.google.com" },
    { node: <BsAmazon />, title: "Amazon", href: "https://www.amazon.com" },
    { node: <TfiMicrosoftAlt />, title: "Microsoft", href: "https://www.microsoft.com" },
    { node: <BsMeta />, title: "Meta", href: "https://www.meta.com" },
    { node: <SiNetflix />, title: "Netflix", href: "https://www.netflix.com" },
  ];

  return (
    <>
      <AppBackground>
        {/* Global background accents (TechApp palette, purple/blue) */}
        {/* <div className="pointer-events-none absolute inset-0 h-full">
          <div className="absolute -top-32 left-1/2 h-[680px] w-[680px] -translate-x-1/2 rounded-full bg-[#6C63FF]/10 blur-[110px]" />
          <div className="absolute -bottom-40 right-[-120px] h-[700px] w-[700px] rounded-full bg-[#3B82F6]/10 blur-[120px]" />
          <div className="absolute inset-0 opacity-[0.18] [background-image:radial-gradient(rgba(255,255,255,0.10)_1px,transparent_1px)] [background-size:26px_26px]" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/10 to-black/80" />
        </div> */}
        <h1 style={{visibility:'hidden'}}>InterviewGenieAI: Train with AI Until You Pass Real Interviews</h1>
        <SEO
          title={t("InterviewGenieAI — Train with AI Until You Pass Real Interviews")}
          description={t("Practice realistic AI interviews, get instant feedback, and start free with 2 AI interviews every month. No credit card required.")}
          canonical="https://interviewgenieai.com/"
        />
        <div style={{zIndex:99999999999999}} className="fixed right-4 top-1/2 -translate-y-1/2 flex flex-col items-center gap-2 p-2 rounded-full border border-white/10 bg-black/20 backdrop-blur-xl shadow-[0_18px_60px_-35px_rgba(0,0,0,0.85)]">
          {/* MORE Tooltip */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <a href="#features" style={{zIndex:99999999999999}} className="rounded-full p-3 text-white/70 hover:text-white hover:bg-white/[0.06] border border-white/0 hover:border-white/10 transition-all">
                  <Blocks size={25}/>
                </a>
              </TooltipTrigger>
              <TooltipContent 
                side="left" 
                arrowColor="bg-gray-800 fill-gray-800" 
                className="transition-all duration-1000 flex flex-col max-w-xs bg-gray-800 border-gray-600 text-white"
              >
                Features
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <a href="#faq" style={{zIndex:99999999999999}} className="rounded-full p-3 text-white/70 hover:text-white hover:bg-white/[0.06] border border-white/0 hover:border-white/10 transition-all">
                  <TableOfContents size={25}/>
                </a>
              </TooltipTrigger>
              <TooltipContent 
                side="left" 
                arrowColor="bg-gray-800 fill-gray-800" 
                className="transition-all duration-1000 flex flex-col max-w-xs bg-gray-800 border-gray-600 text-white"
              >
                FAQ
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <a href="#demo" style={{zIndex:99999999999999}} className="rounded-full p-3 text-white/70 hover:text-white hover:bg-white/[0.06] border border-white/0 hover:border-white/10 transition-all">
                  <GitCompareArrows size={25}/>
                </a>
              </TooltipTrigger>
              <TooltipContent 
                side="left" 
                arrowColor="bg-gray-800 fill-gray-800" 
                className="transition-all duration-1000 flex flex-col max-w-xs bg-gray-800 border-gray-600 text-white"
              >
                Demo
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <a href="#how-it-works" style={{zIndex:99999999999999}} className="rounded-full p-3 text-white/70 hover:text-white hover:bg-white/[0.06] border border-white/0 hover:border-white/10 transition-all">
                  <FaQuestion size={25}/>
                </a>
              </TooltipTrigger>
              <TooltipContent 
                side="left" 
                arrowColor="bg-gray-800 fill-gray-800" 
                className="transition-all duration-1000 flex flex-col max-w-xs bg-gray-800 border-gray-600 text-white"
              >
                How it works
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        
        <DemoPreviewModal isOpen={isOpen} setIsOpen={setIsOpen} />

        {/* Landing navbar */}
        <header data-i18n-ignore="false" className="fixed inset-x-0 top-0 z-[999999] border-b border-white/10 bg-[#05060A]/75 backdrop-blur-2xl">
          <nav className="mx-auto flex h-[68px] max-w-7xl items-center justify-between gap-3 px-4 sm:px-6 lg:px-8" dir="ltr">
            <Link to="/" className="flex min-w-0 items-center gap-3">
              <img src="/GenieAI_2.png" alt="InterviewGenieAI" className="h-9 w-9 shrink-0 object-contain" />
              <span className="hidden text-sm font-bold tracking-wide text-white sm:inline">InterviewGenieAI</span>
            </Link>

            <div className="hidden items-center gap-6 text-sm font-medium text-white/65 lg:flex">
              <a href="#features" className="transition hover:text-white">{t("Features")}</a>
              <a href="#how-it-works" className="transition hover:text-white">{t("How it works")}</a>
              <a href="#demo" className="transition hover:text-white">{t("Demo")}</a>
              <a href="#faq" className="transition hover:text-white">{t("FAQ")}</a>
            </div>

            <div className="flex shrink-0 items-center justify-end gap-2">
              <LanguageSwitcher compact className="shrink-0" />
              <Link
                to="/login"
                className="hidden rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-2 text-sm font-semibold text-white/80 transition hover:bg-white/[0.08] hover:text-white sm:inline-flex"
              >
                {t("Login")}
              </Link>
              <button
                onClick={() => handleSignup("landing_nav_cta")}
                className="rounded-2xl bg-gradient-to-br from-[#8B7CFF] via-[#6C63FF] to-[#3B82F6] px-4 py-2 text-sm font-semibold text-white shadow-[0_16px_45px_-25px_rgba(108,99,255,0.85)] ring-1 ring-white/10 transition hover:shadow-[0_20px_65px_-30px_rgba(108,99,255,0.95)]"
              >
                {t("Start Free")}
              </button>
            </div>
          </nav>
        </header>
        
        {/* Hero Section */}
        <section className="relative mt-[68px] pt-10 sm:pt-14 pb-24 px-6 sm:px-8 overflow-hidden">
          {/* Premium hero background (TechApp palette, purple/blue) */}
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -top-24 -left-24 h-[520px] w-[520px] rounded-full bg-[#6C63FF]/18 blur-[90px]" />
            <div className="absolute -bottom-28 -right-24 h-[560px] w-[560px] rounded-full bg-[#3B82F6]/14 blur-[95px]" />
            <div className="absolute inset-0 bg-[radial-gradient(1200px_600px_at_25%_20%,rgba(255,255,255,0.10),transparent_60%)]" />
            <div className="absolute inset-0 opacity-[0.22] [background-image:radial-gradient(rgba(255,255,255,0.12)_1px,transparent_1px)] [background-size:24px_24px]" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/15 to-black/80" />
          </div>


          {/* <div> */}
            <Threads className="w-full h-full left-0 absolute z-1 object-fill opacity-0.4"  distance={0} enableMouseInteraction={true} />
          {/* </div> */}
          
          <div className="max-w-7xl mx-auto relative z-10">
            <div className="flex flex-col lg:grid lg:grid-cols-2 gap-14 lg:gap-12 items-center min-h-[680px]">
              
              {/* Text Content */}
              <SectionWrapper className="text-center lg:text-left lg:pr-6">
                <motion.div 
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                  className="mb-8"
                >
                  <div data-i18n-ignore="true">
                    <TextType 
                      key={`landing-hero-${i18nLanguage}`}
                      text={tList(["Pass your next interview.", "Train with AI until you pass."])}
                      typingSpeed={75}
                      pauseDuration={1500}
                      showCursor={true}
                      cursorCharacter="|"
                    />
                  </div>
                </motion.div>
                
                <motion.div 
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  className="mb-12"
                >
                  <div data-i18n-ignore="true">
                    <BlurText
                      key={`landing-hero-subtitle-${i18nLanguage}`}
                      text={t("Most candidates fail because they do not know what to say, how to structure answers, or where they are losing points. Practice realistic AI interviews, get instant feedback, and improve every answer before the real one.")}
                      delay={220}
                      animateBy="words"
                      direction="top"
                      className="text-base sm:text-lg text-white/65 max-w-xl mx-auto lg:mx-0 leading-relaxed"
                    />
                  </div>
                </motion.div>
                
                <motion.div 
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start"
                >
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleSignup("landing_cta")}
                    style={{
                      boxShadow: 'inset 0 -4px 10px rgba(255, 255, 255, 0.3), 0 0 40px rgba(108, 99, 255, 0.3)'
                    }}
                    className="px-8 py-3.5 group rounded-2xl bg-gradient-to-br from-[#8B7CFF] via-[#6C63FF] to-[#3B82F6] text-white font-semibold tracking-wide transition-all duration-300 shadow-[0_18px_50px_-22px_rgba(108,99,255,0.55)] hover:shadow-[0_22px_70px_-30px_rgba(108,99,255,0.70)] ring-1 ring-white/10 flex items-center justify-center gap-2 relative overflow-hidden"
                  >
                    <div 
                      className="absolute -bottom-1 left-12 w-1/2 right-0 h-4 rounded-full bg-white/70 blur-[10px]  pointer-events-none"
                    />
                    <span className="relative z-10">Start Free Interview</span>
                    <ArrowRight className="group-hover:animate-pulse group-hover:translate-x-1 transition-transform" size={18} />
                  </motion.button>
                  
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setIsOpen(true)}
                    className="px-8 py-3.5 rounded-2xl backdrop-blur-xl bg-white/[0.04] border border-white/10 text-white/90 font-semibold tracking-wide transition-all duration-300 hover:bg-white/[0.08] hover:border-white/20 flex items-center justify-center gap-2"
                  >
                    <Play size={18} /> Watch demo
                  </motion.button>
                </motion.div>
                <SignupTrustHooks />
              </SectionWrapper>
              
              {/* CardSwap Container */}
              <SectionWrapper className="w-full lg:mt-8">
                <div className="relative w-full max-w-md sm:max-w-lg lg:max-w-none lg:w-full h-[420px] sm:h-[520px] lg:h-[620px] z-50">
                    <CardSwap
                      cardDistance={90}
                      verticalDistance={100}
                      delay={5000}
                      pauseOnHover={true}
                    >
                      <Card>
                        {/* TEXT CARD */}
                        <div style={{zIndex:2}} className="relative p-3 sm:p-4 lg:p-6 rounded-3xl border border-white/10 bg-white/[0.045] backdrop-blur-2xl ring-1 ring-inset ring-white/10 shadow-[0_20px_60px_-35px_rgba(0,0,0,0.85),0_0_90px_rgba(108,99,255,0.10)] w-full h-full overflow-y-auto">
                          <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4 lg:mb-6">
                            <div className="w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10 rounded-full bg-purple-600 flex items-center justify-center flex-shrink-0">
                              <Bot size={12} className="sm:w-4 sm:h-4 lg:w-5 lg:h-5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-xs sm:text-sm lg:text-base truncate">AI Interviewer</p>
                              <p className="text-xs text-gray-400">Question 1 of 5</p>
                            </div>
                          </div>
                          
                          <div className="mb-3 sm:mb-4 lg:mb-6">
                            <p className="text-gray-200 mb-2 text-xs sm:text-sm lg:text-base leading-relaxed">
                              Can you tell me about a challenging project you worked on and how you contributed to its success?
                            </p>
                            <div className="h-1.5 sm:h-2 bg-white/5 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full transition-all duration-700" 
                                style={{ width: '82%' }}
                              ></div>
                            </div>
                          </div>
                          
                          <div className="p-2 sm:p-3 lg:p-4 border border-purple-200/20 bg-purple-500/10 rounded-lg">
                            <h4 className="font-bold mb-2 text-purple-200 text-xs sm:text-sm lg:text-base">Feedback:</h4>
                            <div className="grid grid-cols-2 gap-1 sm:gap-2 text-xs">
                              <div>
                                <strong className="text-gray-300">Structure:</strong>
                                <p className="text-gray-200">Clear</p>
                              </div>
                              <div>
                                <strong className="text-gray-300">Clarity:</strong>
                                <p className="text-gray-200">Good</p>
                              </div>
                              <div>
                                <strong className="text-gray-300">Confidence:</strong>
                                <p className="text-gray-200">Strong</p>
                              </div>
                              <div>
                                <strong className="text-gray-300">Filler Words:</strong>
                                <p className="text-gray-200">Minimal</p>
                              </div>
                            </div>
                            <div className="mt-2 sm:mt-3 flex items-center gap-2">
                              <strong className="text-gray-300 text-xs">Score:</strong>
                              <div className="flex-1 h-1.5 sm:h-2 bg-white/10 rounded-full overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full" style={{ width: '82%' }}></div>
                              </div>
                              <span className="font-bold text-purple-300 text-xs sm:text-sm">82%</span>
                            </div>
                          </div>
                        </div>
                      </Card>
          
                      <Card>
                        {/* VIDEO CARD */}
                        <div className="relative w-full h-full">
                          <div className="relative p-3 sm:p-4 lg:p-6 rounded-3xl border border-white/10 bg-white/[0.045] backdrop-blur-2xl ring-1 ring-inset ring-white/10 shadow-[0_20px_60px_-35px_rgba(0,0,0,0.85),0_0_90px_rgba(108,99,255,0.10)] w-full h-full overflow-y-auto">
                          <div className="absolute right-4 top-4 w-fit px-3 py-1 rounded-full border border-teal-300/25 bg-teal-500/10 text-teal-200 text-xs font-semibold tracking-wide backdrop-blur-md">Coming Soon</div>
                          <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center">
                              <Video size={20} />
                            </div>
                            <div>
                              <p className="font-medium">Video Analysis</p>
                              <p className="text-xs text-gray-400">Review your performance</p>
                            </div>
                          </div>
                          
                          {/* Video Player with Analysis Overlay */}
                          <div className="relative mb-6 rounded-xl overflow-hidden">
                            <video 
                              className="w-full h-64 object-cover"
                              controls
                              poster="https://images.unsplash.com/photo-1575936123452-b67c3203c357?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8aW1hZ2V8ZW58MHx8MHx8fDA%3D&w=1000&q=80"
                            >
                              <source src="" type="video/mp4" />
                              Your browser does not support the video tag.
                            </video>
                            
                            {/* Analysis Overlay Elements */}
                            <div className="absolute top-3 right-3 bg-black/70 rounded-lg p-2 text-xs">
                              <div className="flex items-center gap-1 text-green-400">
                                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                                <span>Analyzing...</span>
                              </div>
                            </div>
                            
                            {/* Emotion Indicators */}
                            <div className="absolute bottom-3 left-3 flex gap-2">
                              <div className="bg-black/70 rounded-full p-2 flex items-center gap-1">
                                <div className="w-2 h-2 rounded-full bg-green-400"></div>
                                <span className="text-xs">Confidence: 78%</span>
                              </div>
                              <div className="bg-black/70 rounded-full p-2 flex items-center gap-1">
                                <div className="w-2 h-2 rounded-full bg-yellow-400"></div>
                                <span className="text-xs">Eye Contact: 65%</span>
                              </div>
                            </div>
                            
                            {/* Timeline Analysis Markers */}
                            <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
                              <div className="absolute h-full bg-purple-500/70 w-1/4 left-0"></div>
                              <div className="absolute h-full bg-yellow-500/70 w-1/4 left-1/4"></div>
                              <div className="absolute h-full bg-green-500/70 w-1/4 left-2/4"></div>
                              <div className="absolute h-full bg-red-500/70 w-1/4 left-3/4"></div>
                            </div>
                          </div>
                          
                          {/* Video Analysis Metrics */}
                          <div className="rounded-3xl border border-purple-200/20 bg-purple-500/10 backdrop-blur-xl p-4 ring-1 ring-inset ring-white/10">
                            <h4 className="font-bold mb-3 text-purple-200">Performance Analysis</h4>
                            
                            <div className="grid grid-cols-2 gap-4 mb-4">
                              <div>
                                <div className="flex justify-between items-center mb-1">
                                  <span className="text-sm text-gray-300">Confidence Level</span>
                                  <span className="text-sm font-bold text-green-400">78%</span>
                                </div>
                                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-gradient-to-r from-green-500 to-green-300 rounded-full" 
                                    style={{ width: '78%' }}
                                  ></div>
                                </div>
                              </div>
                              
                              <div>
                                <div className="flex justify-between items-center mb-1">
                                  <span className="text-sm text-gray-300">Eye Contact</span>
                                  <span className="text-sm font-bold text-yellow-400">65%</span>
                                </div>
                                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-gradient-to-r from-yellow-500 to-yellow-300 rounded-full" 
                                    style={{ width: '65%' }}
                                  ></div>
                                </div>
                              </div>
                              
                              <div>
                                <div className="flex justify-between items-center mb-1">
                                  <span className="text-sm text-gray-300">Pace & Rhythm</span>
                                  <span className="text-sm font-bold text-blue-400">82%</span>
                                </div>
                                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-gradient-to-r from-blue-500 to-blue-300 rounded-full" 
                                    style={{ width: '82%' }}
                                  ></div>
                                </div>
                              </div>
                              
                              <div>
                                <div className="flex justify-between items-center mb-1">
                                  <span className="text-sm text-gray-300">Body Language</span>
                                  <span className="text-sm font-bold text-purple-400">71%</span>
                                </div>
                                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-gradient-to-r from-purple-500 to-purple-300 rounded-full" 
                                    style={{ width: '71%' }}
                                  ></div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Detailed Feedback */}
                            <div className="mt-4">
                              <h5 className="font-medium text-gray-300 mb-2">Key Observations:</h5>
                              <ul className="text-xs space-y-1 text-gray-200">
                                <li className="flex items-start gap-2">
                                  <div className="w-1 h-1 mt-1.5 rounded-full bg-green-400 flex-shrink-0"></div>
                                  <span>Good posture and hand gestures when explaining complex topics</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <div className="w-1 h-1 mt-1.5 rounded-full bg-yellow-400 flex-shrink-0"></div>
                                  <span>Try to maintain more consistent eye contact with the camera</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <div className="w-1 h-1 mt-1.5 rounded-full bg-green-400 flex-shrink-0"></div>
                                  <span>Appropriate speaking pace with good vocal variety</span>
                                </li>
                                <li className="flex items-start gap-2">
                                  <div className="w-1 h-1 mt-1.5 rounded-full bg-red-400 flex-shrink-0"></div>
                                  <span>Avoid looking down too frequently when referring to notes</span>
                                </li>
                              </ul>
                            </div>
                            
                            {/* Overall Score */}
                            <div className="mt-4 pt-3 border-t border-white/10 flex items-center gap-2">
                              <strong className="text-gray-300">Overall Performance Score:</strong>
                              <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full" style={{ width: '76%' }}></div>
                              </div>
                              <span className="font-bold text-purple-300">76%</span>
                            </div>
                          </div>
                          </div>
                          {/* Background effects */}
                          <div className="absolute -top-2 -right-2 sm:-top-4 sm:-right-4 w-8 h-8 sm:w-16 sm:h-16 lg:w-28 lg:h-28 bg-purple-500/20 rounded-full blur-xl"></div>
                          <div className="absolute -bottom-2 -left-2 sm:-bottom-4 sm:-left-4 w-6 h-6 sm:w-12 sm:h-12 lg:w-24 lg:h-24 bg-indigo-500/20 rounded-full blur-xl"></div>
                        </div>
                      </Card>
          
                      <Card>
                        {/* PHONE CARD */}
                        <div className="relative w-full h-full">
                          <div className="relative p-3 sm:p-4 lg:p-6 rounded-3xl border border-white/10 bg-white/[0.045] backdrop-blur-2xl ring-1 ring-inset ring-white/10 shadow-[0_20px_60px_-35px_rgba(0,0,0,0.85),0_0_90px_rgba(108,99,255,0.10)] w-full h-full overflow-y-auto">
                          <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                              <Mic size={20} />
                            </div>
                            <div>
                              <p className="font-medium">Phone Interview Simulation</p>
                              <p className="text-xs text-gray-400">Practice phone screening rounds</p>
                            </div>
                          </div>
                          
                          {/* Phone Interface */}
                          <div className="mb-6 rounded-2xl bg-gradient-to-b from-gray-900 to-black p-6 border border-gray-700">
                            {/* Call Header */}
                            <div className="text-center mb-8">
                              <div className="text-lg font-medium mb-1">TechCorp Inc. Recruiter</div>
                              <div className="text-sm text-gray-400">+1 (555) 123-4567</div>
                              <div className="flex items-center justify-center mt-2 text-xs text-green-400">
                                <div className="w-2 h-2 rounded-full bg-green-400 mr-1 animate-pulse"></div>
                                <span>Connected • 12:45</span>
                              </div>
                            </div>
                            
                            {/* Call Timer */}
                            <div className="flex justify-center mb-8">
                              <div className="bg-black/50 rounded-full py-3 px-6 border border-gray-700">
                                <span className="text-xl font-mono">15:32</span>
                              </div>
                            </div>
                            
                            {/* Call Controls */}
                            <div className="flex justify-center gap-6">
                              <button className="w-12 h-12 rounded-full bg-red-500 flex items-center justify-center hover:bg-red-600 transition-colors">
                                <MicOff size={20} />
                              </button>
                              <button className="w-16 h-16 rounded-full bg-green-600 flex items-center justify-center hover:bg-green-700 transition-colors">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                              <button className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center hover:bg-blue-700 transition-colors">
                                <Volume2 size={20} />
                              </button>
                            </div>
                            
                            {/* Call Status Indicators */}
                            <div className="mt-8 grid grid-cols-2 gap-4 text-xs">
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-green-400"></div>
                                <span className="text-gray-400">Connection: Excellent</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-green-400"></div>
                                <span className="text-gray-400">Audio: Clear</span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Conversation Analysis */}
                          <div className="rounded-3xl border border-blue-200/20 bg-blue-500/10 backdrop-blur-xl p-4 ring-1 ring-inset ring-white/10">
                            <h4 className="font-bold mb-3 text-blue-200">Call Analysis</h4>
                            
                            <div className="grid grid-cols-2 gap-4 mb-4">
                              <div>
                                <div className="flex justify-between items-center mb-1">
                                  <span className="text-sm text-gray-300">Clarity</span>
                                  <span className="text-sm font-bold text-green-400">85%</span>
                                </div>
                                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-gradient-to-r from-green-500 to-green-300 rounded-full" 
                                    style={{ width: '85%' }}
                                  ></div>
                                </div>
                              </div>
                              
                              <div>
                                <div className="flex justify-between items-center mb-1">
                                  <span className="text-sm text-gray-300">Pacing</span>
                                  <span className="text-sm font-bold text-blue-400">78%</span>
                                </div>
                                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-gradient-to-r from-blue-500 to-blue-300 rounded-full" 
                                    style={{ width: '78%' }}
                                  ></div>
                                </div>
                              </div>
                              
                              <div>
                                <div className="flex justify-between items-center mb-1">
                                  <span className="text-sm text-gray-300">Response Time</span>
                                  <span className="text-sm font-bold text-yellow-400">68%</span>
                                </div>
                                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-gradient-to-r from-yellow-500 to-yellow-300 rounded-full" 
                                    style={{ width: '68%' }}
                                  ></div>
                                </div>
                              </div>
                              
                              <div>
                                <div className="flex justify-between items-center mb-1">
                                  <span className="text-sm text-gray-300">Professionalism</span>
                                  <span className="text-sm font-bold text-purple-400">82%</span>
                                </div>
                                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-gradient-to-r from-purple-500 to-purple-300 rounded-full" 
                                    style={{ width: '82%' }}
                                  ></div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Conversation Transcript Preview */}
                            <div className="mt-4">
                              <h5 className="font-medium text-gray-300 mb-2">Conversation Highlights:</h5>
                              <div className="text-xs space-y-2 max-h-32 overflow-y-auto">
                                <div className="bg-black/20 p-2 rounded">
                                  <div className="text-blue-400 font-medium">Interviewer:</div>
                                  <p className="text-gray-200">"Can you walk me through your experience with React and Node.js?"</p>
                                </div>
                                <div className="bg-black/20 p-2 rounded">
                                  <div className="text-green-400 font-medium">You:</div>
                                  <p className="text-gray-200">"I've worked with React for about 3 years, building several SPAs and recently working with Next.js for SSR applications."</p>
                                </div>
                                <div className="bg-black/20 p-2 rounded">
                                  <div className="text-blue-400 font-medium">Interviewer:</div>
                                  <p className="text-gray-200">"How do you handle state management in large applications?"</p>
                                </div>
                              </div>
                            </div>
                            
                            {/* Filler Word Analysis */}
                            <div className="mt-4 pt-3 border-t border-white/10">
                              <div className="flex justify-between items-center mb-2">
                                <span className="text-sm text-gray-300">Filler Words Detected:</span>
                                <span className="text-sm font-bold text-red-400">12</span>
                              </div>
                              <div className="flex flex-wrap gap-1 text-xs">
                                <span className="bg-red-400/20 text-red-300 px-2 py-1 rounded">um (5)</span>
                                <span className="bg-red-400/20 text-red-300 px-2 py-1 rounded">like (3)</span>
                                <span className="bg-red-400/20 text-red-300 px-2 py-1 rounded">you know (2)</span>
                                <span className="bg-red-400/20 text-red-300 px-2 py-1 rounded">actually (2)</span>
                              </div>
                            </div>
                            
                            {/* Overall Score */}
                            <div className="mt-4 pt-3 border-t border-white/10 flex items-center gap-2">
                              <strong className="text-gray-300">Call Performance Score:</strong>
                              <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full" style={{ width: '80%' }}></div>
                              </div>
                              <span className="font-bold text-blue-300">80%</span>
                            </div>
                          </div>
                          </div>
                          {/* Background effects */}
                          <div className="absolute -top-2 -right-2 sm:-top-4 sm:-right-4 w-8 h-8 sm:w-16 sm:h-16 lg:w-28 lg:h-28 bg-blue-500/20 rounded-full blur-xl"></div>
                          <div className="absolute -bottom-2 -left-2 sm:-bottom-4 sm:-left-4 w-6 h-6 sm:w-12 sm:h-12 lg:w-24 lg:h-24 bg-indigo-500/20 rounded-full blur-xl"></div>
                        </div>
                      </Card>
                    </CardSwap>
                </div>
              </SectionWrapper>
            </div>
          </div>
        </section>
        

        {/* Conversion Trust + Results Preview */}
        <section className="relative py-16 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#6C63FF]/[0.035] to-transparent" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
              {[
                { value: "Realistic", label: "AI interviews for voice, chat, behavioral, technical, and panel practice" },
                { value: "Instant", label: "Feedback on structure, clarity, confidence, filler words, and relevance" },
                { value: "Repeat", label: "Improve answer by answer until your interview score becomes consistent" }
              ].map((stat, index) => (
                <StaggerItem key={stat.value} index={index}>
                  <GlassCard className="p-6 text-center h-full">
                    <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-white to-[#8B7CFF] bg-clip-text text-transparent mb-2">{stat.value}</div>
                    <p className="text-sm text-white/65 leading-relaxed">{stat.label}</p>
                  </GlassCard>
                </StaggerItem>
              ))}
            </StaggerContainer>

            <GlassCard className="p-6 md:p-8 lg:p-10">
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#6C63FF]/10 border border-[#6C63FF]/20 text-[#B7B2FF] text-sm font-semibold mb-5">
                    <Target size={16} /> Real result example
                  </div>
                  <h2 className="text-3xl md:text-5xl font-bold text-white mb-5 leading-tight">
                    Stop guessing why you failed. See exactly what to improve.
                  </h2>
                  <p className="text-white/65 text-lg leading-relaxed mb-6">
                    InterviewGenieAI turns every practice answer into a clear coaching report, so users know what to fix before facing a recruiter or hiring manager.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button onClick={() => handleSignup("landing_cta")} className="px-6 py-3 rounded-2xl bg-gradient-to-br from-[#8B7CFF] to-[#6C63FF] text-white font-semibold flex items-center justify-center gap-2 shadow-[0_18px_50px_-25px_rgba(108,99,255,0.75)]">
                      Start Free Interview <ArrowRight size={18} />
                    </button>
                    <button onClick={() => setIsOpen(true)} className="px-6 py-3 rounded-2xl bg-white/[0.04] border border-white/10 text-white font-semibold flex items-center justify-center gap-2 hover:bg-white/[0.08]">
                      <Play size={18} /> Watch demo
                    </button>
                  </div>
                </div>

                <div className="grid gap-4">
                  <div className="rounded-3xl border border-red-400/20 bg-red-500/[0.06] p-5">
                    <p className="text-red-200 font-semibold mb-3">Before InterviewGenieAI</p>
                    <ul className="space-y-2 text-sm text-white/65">
                      <li className="flex gap-2"><Minus size={16} className="text-red-300 mt-0.5" /> Weak answer with no STAR structure</li>
                      <li className="flex gap-2"><Minus size={16} className="text-red-300 mt-0.5" /> Too vague, no metrics, no clear impact</li>
                      <li className="flex gap-2"><Minus size={16} className="text-red-300 mt-0.5" /> Confidence score drops under pressure</li>
                    </ul>
                  </div>
                  <div className="rounded-3xl border border-emerald-400/20 bg-emerald-500/[0.06] p-5">
                    <p className="text-emerald-200 font-semibold mb-3">After AI coaching</p>
                    <ul className="space-y-2 text-sm text-white/75">
                      <li className="flex gap-2"><CheckCircle size={16} className="text-emerald-300 mt-0.5" /> Structured answer using STAR method</li>
                      <li className="flex gap-2"><CheckCircle size={16} className="text-emerald-300 mt-0.5" /> Clear business impact and stronger keywords</li>
                      <li className="flex gap-2"><CheckCircle size={16} className="text-emerald-300 mt-0.5" /> Final score: <span className="font-bold text-white">84/100</span> — interview ready</li>
                    </ul>
                  </div>
                </div>
              </div>
            </GlassCard>
          </div>
        </section>

        {/* Logo Carousel */}
        <section className="h-full relative">
          
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 ">
            <div className="inline-flex py-20 justify-center w-full items-center gap-3 mb-6">
              <motion.div 
              initial={{ opacity: 0, x: -500 ,scale:5}}
              whileInView={{ opacity: 1, x: 0, scale:1 }}
              transition={{ duration: 3, ease: [0.22, 1, 0.36, 1] }}
              className="w-8 h-px bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent" />
              <span className="text-sm font-medium tracking-wider text-[#6C63FF] uppercase">
                Inspired by interviews at leading tech companies
              </span>
              <motion.div 
              initial={{ opacity: 0, x: 500 ,scale:5}}
              whileInView={{ opacity: 1, x: 0, scale:1 }}
              transition={{ duration: 3, ease: [0.22, 1, 0.36, 1] }}
              className="w-8 h-px bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent" />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 100 ,scale:5}}
              whileInView={{ opacity: 1, y: 0, scale:1 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="h-32 relative "
            >
              <LogoLoop
                logos={techLogos}
                speed={80}
                direction="left"
                logoHeight={80}
                gap={120}
                pauseOnHover
                scaleOnHover
                fadeOut
                
                fadeOutColor="black"
              />
            </motion.div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-20 relative overflow-hidden">

          {/*  */}
          <SEO
            title="InterviewGenieAI Features — AI Interviews, Feedback, Resume AI & Analytics"
            description="Explore InterviewGenieAI features including voice AI interviews, chat interviews, real-time feedback, final scoring, resume insights, progress tracking, badges, and analytics."
            canonical="https://interviewgenieai.com/features"
          />
          
          <a href='/terms-of-service' className="hidden"/>
          
          <a href='/privacy-policy' className="hidden"/>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

            {/* Features Section */}
            <section className=" relative h-full overflow-hidden">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header */}
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  // viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                  className="text-center mb-32"
                >
                  <div className="inline-flex items-center gap-3 mb-6">
                  
                    <motion.div 
                    initial={{ opacity: 0, x:-500 ,scale:5}}
                    whileInView={{ opacity: 1, x: 0, scale:1 }}
                    transition={{ duration: 3, ease: [0.22, 1, 0.36, 1] }}
                    className="w-8 h-px bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent" />
                    <span className="text-sm font-medium tracking-wider text-[#6C63FF] uppercase">
                      Platform Capabilities
                    </span>
                    <motion.div 
                    initial={{ opacity: 0, x: 500 ,scale:5}}
                    whileInView={{ opacity: 1, x: 0, scale:1 }}
                    transition={{ duration: 3, ease: [0.22, 1, 0.36, 1] }}
                    className="w-8 h-px bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent" />
                  </div>
                  
                  <motion.h2 
                    initial={{ opacity: 0, y: 100 ,scale:5}}
                    whileInView={{ opacity: 1, y: 0, scale:1 }}
                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-8">
                    <span 
                    className="inline-block bg-gradient-to-r from-white via-white to-white/90 bg-clip-text text-transparent">
                      Engineered for
                    </span>
                    <br />
                    <span
                    className="bg-gradient-to-r from-[#6C63FF] to-[#8B7CFF] bg-clip-text text-transparent">
                      Interview Mastery
                    </span>
                  </motion.h2>
                  
                  <motion.p 
                  initial={{ y: 200 }}
                  whileInView={{ y: 0 }}
                  transition={{ duration: 2, ease: [0.22, 1, 0.36, 1] }}
                  className="text-xl text-[#EDEDED]/80 max-w-2xl mx-auto leading-relaxed">
                    Every feature meticulously designed to transform your interview performance with surgical precision
                  </motion.p>
                  
                  <motion.div 
                  initial={{opacity:1}}
                  viewPort={{once:true}}
                  style={{marginTop:"20px",display:"flex",justifyContent:"center", width: '100%', height: '600px', position: 'absolute', zIndex:0 }}>
                    <LightRays
                      raysOrigin="top-center"
                      raysColor="white"
                      raysSpeed={1.5}
                      lightSpread={0.8}
                      rayLength={1.2}
                      followMouse={true}
                      mouseInfluence={0.1}
                      noiseAmount={0.1}
                      distortion={0.05}
                      className="custom-rays"
                    />
                      <span className="w-1/2 h-2 bg-white blur-sm z-50 shadow-[0px_0px_10px_white] absolute top-0"></span>
                    <div className=" w-2/3 h-20 bg-white/50 blur-3xl z-50 absolute top-0">
                    </div>
                  </motion.div>
                </motion.div>
            
                {/* Feature Rows - Alternating Layout with Images */}
                <div className="space-y-40">
                  {[
                    { 
                      icon: Zap, 
                      title: "Real-time AI Feedback", 
                      description: "Know exactly what to improve after every answer: structure, clarity, confidence, filler words, and role relevance.",
                      image: "/real-time-feedback.png",
                      alt: "AI feedback dashboard showing real-time scoring metrics"
                    },
                    { 
                      icon: FileText, 
                      title: "Resume-to-Interview", 
                      description: "Turn your resume into realistic interview questions, so your practice matches the experience recruiters will actually ask about.",
                      image: "/resume-to-interview.png",
                      alt: "Resume analysis interface transforming content into interview questions"
                    },
                    { 
                      icon: Video, 
                      title: "Multi-modal Analysis", 
                      description: "Practice the way real interviews happen: voice, chat, technical screens, behavioral questions, and video insights when enabled.",
                      image: "/multi-modal-analysis_pgt4fj.png",
                      alt: "Multiple interview mode analysis panels showing different feedback types"
                    },
                    { 
                      icon: BookOpen, 
                      title: "Role-Specific Question Banks", 
                      description: "Prepare for the exact role you want with questions organized by job level, industry, company style, and interview category.",
                      image: "/role-specific-question-banks.png",
                      alt: "Organized question bank interface with role-based filtering"
                    },
                    { 
                      icon: BarChart3, 
                      title: "Deep Performance Analytics", 
                      description: "Track your score, weak areas, confidence, and improvement streaks so you know when you are truly ready.",
                      image: "/deep-performance-analytics_kkd1va.png",
                      alt: "Performance analytics dashboard with progress charts and metrics"
                    },
                    { 
                      icon: Eye, 
                      title: "Video Feedback & Insights", 
                      description: "See how you sound and present yourself, then fix the small delivery mistakes that can cost you offers.",
                      image: "/video-feedback-insights.png",
                      alt: "Video analysis interface with body language insights overlay"
                    }
                  ].map((feature, index) => {
                    const isLeft = index % 2 === 0;
                    
                    return (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: isLeft ? -80 : 80 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        // viewport={{ once: true, margin: "-120px" }}
                        transition={{
                          duration: 0.9,
                          ease: [0.22, 1, 0.36, 1]
                        }}
                        className="relative"
                      >
                        {/* Connection Line (Subtle) */}
                        {index < 5 && (
                          <div className="absolute top-full left-1/2 w-px h-20 -translate-x-1/2 hidden lg:block">
                            <div className="w-full h-full bg-gradient-to-b from-[#6C63FF]/20 via-transparent to-transparent" />
                          </div>
                        )}
                        
                        {/* Feature Row Container - Responsive Layout */}
                        <div className={`flex flex-col lg:flex-row items-start lg:items-center ${isLeft ? '' : 'lg:flex-row-reverse'} gap-12 lg:gap-24`}>
                          
                          {/* Text Content Column */}
                          <motion.div 
                          initial={isLeft ? { opacity: 0, x: -100 } : { opacity: 0, x: 100 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{
                            duration: 2,
                            ease: [0.22, 1, 0.36, 1]
                          }}
                          className={`lg:w-1/2 ${isLeft ? 'lg:pr-12' : 'lg:pl-12'}`}>
                            {/* Icon Container */}
                            <motion.div
                              whileHover={{ scale: 1.06 }}
                              transition={{ duration: 0.35, ease: "easeOut" }}
                              className="mb-8"
                            >
                              <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-white/5 border border-white/10 relative group">
                                {/* Subtle Glow Effect */}
                                <div className="absolute inset-0 rounded-xl bg-[#6C63FF]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                <feature.icon className="w-7 h-7 text-[#6C63FF] relative z-10" />
                              </div>
                            </motion.div>
                            
                            {/* Text Content */}
                            <div>
                              <h3 className="text-3xl md:text-4xl font-bold text-white mb-6 tracking-tight">
                                {feature.title}
                              </h3>
                              
                              <p className="text-lg text-[#EDEDED]/75 leading-relaxed mb-8 lg:mb-0">
                                {feature.description}
                              </p>
                            </div>
                          </motion.div>
                          
                          {/* Image Column */}
                          <div className={`lg:w-1/2 ${isLeft ? 'lg:pl-12' : 'lg:pr-12'}`}>
                            <motion.div
                              initial={{ opacity: 0, scale: 0.95 }}
                              whileInView={{ opacity: 1, scale: 1 }}
                              // viewport={{ once: true }}
                              transition={{ 
                                duration: 0.8, 
                                delay: 0.3,
                                ease: [0.22, 1, 0.36, 1]
                              }}
                              className="relative"
                            >
                              {/* Desktop/Large Screen Image */}
                              <div className="hidden lg:block">
                                <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-br from-white/5 to-white/[0.02]">
                                  {/* Image Container with Platinum Border */}
                                  <div className="relative aspect-[16/10] overflow-hidden">
                                    {/* Platinum Rim Light */}
                                    <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent pointer-events-none" />
                                    
                                    {/* Image with Premium Loading */}
                                    <img
                                      src={feature.image}
                                      alt={feature.alt}
                                      className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                                      loading="lazy"
                                    />
                                    
                                    {/* Subtle Overlay Gradient */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none" />
                                  </div>
                                  
                                  {/* Corner Accents */}
                                  <div className="absolute top-0 left-0 w-6 h-px bg-gradient-to-r from-[#6C63FF] to-transparent" />
                                  <div className="absolute top-0 left-0 h-6 w-px bg-gradient-to-b from-[#6C63FF] to-transparent" />
                                  <div className="absolute bottom-0 right-0 w-6 h-px bg-gradient-to-l from-[#6C63FF] to-transparent" />
                                  <div className="absolute bottom-0 right-0 h-6 w-px bg-gradient-to-t from-[#6C63FF] to-transparent" />
                                </div>
                              </div>
                              
                              {/* Mobile/Small Screen Image (Blurry Effect) */}
                              <div className="lg:hidden">
                                <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-br from-white/5 to-white/[0.02]">
                                  <div className="relative aspect-[4/3] overflow-hidden">
                                    {/* Blur Overlay for Mobile */}
                                    <div className="absolute inset-0 backdrop-blur-md z-10 pointer-events-none" />
                                    
                                    {/* Image with Reduced Opacity */}
                                    <img
                                      src={feature.image}
                                      alt={feature.alt}
                                      className="w-full h-full object-cover opacity-70"
                                      loading="lazy"
                                    />
                                    
                                    {/* Stronger Gradient Overlay for Mobile */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent pointer-events-none" />
                                    
                                    {/* Mobile Warning Indicator */}
                                    <div className="absolute top-4 right-4 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm flex items-center gap-2 z-20">
                                      <div className="w-2 h-2 rounded-full bg-[#6C63FF] animate-pulse" />
                                      <span className="text-xs text-white/80">View on larger screen</span>
                                    </div>
                                  </div>
                                  
                                  {/* Mobile Specific Message */}
                                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-30">
                                    <div className="text-center px-6">
                                      <div className="w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center mx-auto mb-3">
                                        <Eye className="w-5 h-5 text-white" />
                                      </div>
                                      <p className="text-sm text-white/90 font-medium">
                                        Enhanced visualization available on desktop
                                      </p>
                                      <p className="text-xs text-white/60 mt-1">
                                        Switch to larger screen for detailed view
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Mobile Feature Context */}
                                <div className="mt-6 p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm">
                                  <div className="flex items-center gap-3 mb-2">
                                    <div className="w-8 h-8 rounded-lg bg-[#6C63FF]/20 flex items-center justify-center">
                                      <feature.icon className="w-4 h-4 text-[#6C63FF]" />
                                    </div>
                                    <span className="text-sm font-medium text-white">
                                      {feature.title}
                                    </span>
                                  </div>
                                  <p className="text-sm text-white/70">
                                    {feature.description.split('. ')[0]}.
                                  </p>
                                </div>
                              </div>
                            </motion.div>
                          </div>
                        </div>
                        {/* Advanced Feature Separator */}
                        {index < 6 && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            transition={{ delay: 0.6 }}
                            className="hidden lg:flex justify-center mt-24"
                          >
                            <div className="relative w-48 h-px">
                              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#6C63FF]/40 to-transparent" />
                              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-[#6C63FF]/20 rounded-full border border-[#6C63FF]/30" />
                            </div>
                          </motion.div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
            
                {/* Advanced Features (Bottom Block) */}
                <div className="mt-20 space-y-40">
                  {[
                    { 
                      icon: Target, 
                      title: "Personalized Improvement Plan", 
                      description: "AI-generated skill maps, practice guidelines, daily speaking drills, and confidence exercises tailored to your gaps. Every recommendation is backed by performance data.",
                      image: "/personalized-improvement-plan_ll5ofr.png",
                      alt: "Personalized improvement dashboard showing skill maps and practice recommendations"
                    },
                    { 
                      icon: Download, 
                      title: "Professional Output & Downloads", 
                      description: "Export PDF performance reports, suggested scripts, session notes, and improvement roadmaps. Shareable insights for mentors and career coaches.",
                      image: "/professional-output-downloads.png",
                      alt: "Export interface showing multiple document formats and download options"
                    }
                  ].map((feature, index) => {
                    const isLeft = index % 2 === 0;
                    
                    return (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: isLeft ? -80 : 80 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        // viewport={{ once: true, margin: "-120px" }}
                        transition={{
                          duration: 1.05,
                          ease: [0.22, 1, 0.36, 1]
                        }}
                        className="relative"
                      >
                        {/* Feature Row Container - Responsive Layout */}
                        <div className={`flex flex-col lg:flex-row items-start lg:items-center ${isLeft ? 'lg:flex-row-reverse':'' } gap-8 lg:gap-24`}>
                          
                          {/* Text Content Column - Appears first on mobile */}
                          <div className={`lg:w-1/2 ${isLeft ? 'lg:pr-12' : 'lg:pl-12'} order-1`}>
                            {/* Enhanced Icon Container */}
                            <motion.div
                              whileHover={{ scale: 1.08 }}
                              transition={{ duration: 0.4, ease: "easeOut" }}
                              className="mb-8"
                            >
                              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/5 border border-white/10 relative group">
                                {/* Platinum Rim Light */}
                                <div className="absolute -inset-0.5 rounded-2xl bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                
                                {/* Subtle Purple Accent Glow */}
                                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#6C63FF]/10 to-transparent opacity-50 blur-sm" />
                                
                                <feature.icon className="w-8 h-8 text-[#6C63FF] relative z-10" />
                              </div>
                            </motion.div>
                            
                            {/* Enhanced Text Content */}
                            <div>
                              <div className="inline-flex items-center gap-3 mb-4">
                                <motion.div 
                                initial={{ opacity: 0, x: -100 ,scale:5}}
                                whileInView={{ opacity: 1, x: 0, scale:1 }}
                                transition={{ duration: 3, ease: [0.22, 1, 0.36, 1] }}
                                className="w-8 h-px bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent" />
                                <span className="text-sm font-medium tracking-wider text-[#6C63FF] uppercase">
                                  Advanced Capability
                                </span>
                                <motion.div 
                                initial={{ opacity: 0, x: 100 ,scale:5}}
                                whileInView={{ opacity: 1, x: 0, scale:1 }}
                                transition={{ duration: 3, ease: [0.22, 1, 0.36, 1] }}
                                className="w-8 h-px bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent" />
                              </div>
                              
                              <h3 className="text-3xl md:text-4xl font-bold text-white mb-6 tracking-tight">
                                {feature.title}
                              </h3>
                              
                              <p className="text-lg text-[#EDEDED]/75 leading-relaxed lg:max-w-xl">
                                {feature.description}
                              </p>
                            </div>
                          </div>
                          
                          {/* Image Column - Appears second (under) on mobile */}
                          <div className={`lg:w-1/2 ${isLeft ? 'lg:pl-12' : 'lg:pr-12'} order-2 lg:order-none`}>
                            <motion.div
                              initial={{ opacity: 0, scale: 0.95 }}
                              whileInView={{ opacity: 1, scale: 1 }}
                              viewport={{ once: true }}
                              transition={{ 
                                duration: 0.8, 
                                delay: 0.4,
                                ease: [0.22, 1, 0.36, 1]
                              }}
                              className="relative"
                            >
                              {/* Desktop/Large Screen Image - Full Quality */}
                              <div className="hidden lg:block">
                                <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-br from-white/5 to-white/[0.02] premium-hover">
                                  {/* Premium Image Container */}
                                  <div className="relative aspect-[16/9] overflow-hidden">
                                    {/* Platinum Overlay Effects */}
                                    <div className="absolute inset-0 bg-gradient-to-br from-white/[0.03] via-transparent to-transparent pointer-events-none" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none" />
                                    
                                    {/* Advanced Feature Badge */}
                                    <div className="absolute top-4 left-4 z-20">
                                      <div className="px-3 py-1.5 rounded-full bg-[#6C63FF]/20 border border-[#6C63FF]/30 backdrop-blur-sm flex items-center gap-2">
                                        <div className="w-2 h-2 rounded-full bg-[#6C63FF] animate-pulse" />
                                        <span className="text-xs font-medium text-[#6C63FF]">Premium Feature</span>
                                      </div>
                                    </div>
                                    
                                    {/* Main Image */}
                                    <img
                                      src={feature.image}
                                      alt={feature.alt}
                                      className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                                      loading="lazy"
                                    />
                                  </div>
                                  
                                  {/* Corner Accents */}
                                  <div className="absolute top-0 left-0 w-8 h-px bg-gradient-to-r from-[#6C63FF] via-white/30 to-transparent" />
                                  <div className="absolute top-0 left-0 h-8 w-px bg-gradient-to-b from-[#6C63FF] via-white/30 to-transparent" />
                                  <div className="absolute bottom-0 right-0 w-8 h-px bg-gradient-to-l from-[#6C63FF] via-white/30 to-transparent" />
                                  <div className="absolute bottom-0 right-0 h-8 w-px bg-gradient-to-t from-[#6C63FF] via-white/30 to-transparent" />
                                </div>
                              </div>
                              
                              {/* Mobile/Small Screen Image - Blurry under content */}
                              <div className="lg:hidden">
                                <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-br from-white/5 to-white/[0.02] mt-8">
                                  <div className="relative aspect-[4/3] overflow-hidden">
                                    {/* Strong Blur Overlay for Mobile */}
                                    <div className="absolute inset-0 backdrop-blur-lg z-10 pointer-events-none" />
                                    
                                    {/* Reduced Quality Image */}
                                    <img
                                      src={feature.image}
                                      alt={feature.alt}
                                      className="w-full h-full object-cover opacity-60 saturate-50"
                                      loading="lazy"
                                    />
                                    
                                    {/* Dark Gradient Overlay */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent pointer-events-none" />
                                    
                                    {/* Mobile Feature Indicator */}
                                    <div className="absolute top-4 right-4 z-20">
                                      <div className="px-3 py-1.5 rounded-full bg-black/40 border border-white/20 backdrop-blur-md flex items-center gap-2">
                                        <div className="w-2 h-2 rounded-full bg-[#6C63FF] animate-pulse" />
                                        <span className="text-xs text-white/80">Advanced Feature</span>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {/* Enhanced Mobile Message */}
                                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-30 px-6">
                                    <div className="text-center">
                                      <div className="w-12 h-12 rounded-full bg-white/10 border border-white/20 backdrop-blur-lg flex items-center justify-center mx-auto mb-4">
                                        <Sparkles className="w-6 h-6 text-[#6C63FF]" />
                                      </div>
                                      <p className="text-sm text-white font-medium mb-2">
                                        Premium Visualization
                                      </p>
                                      <p className="text-xs text-white/70 max-w-xs mx-auto">
                                        Switch to desktop for detailed interface preview and enhanced visuals
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Mobile Feature Summary Card */}
                                <div className="mt-4 p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm">
                                  <div className="flex items-center gap-3 mb-3">
                                    <div className="w-10 h-10 rounded-lg bg-[#6C63FF]/20 flex items-center justify-center">
                                      <feature.icon className="w-5 h-5 text-[#6C63FF]" />
                                    </div>
                                    <div>
                                      <span className="text-sm font-medium text-[#6C63FF] uppercase tracking-wider">
                                        Advanced Feature
                                      </span>
                                      <p className="text-base font-semibold text-white">
                                        {feature.title}
                                      </p>
                                    </div>
                                  </div>
                                  <p className="text-sm text-white/70">
                                    Available in full detail on desktop. Includes enhanced visuals, interactive elements, and comprehensive interface previews.
                                  </p>
                                </div>
                              </div>
                            </motion.div>
                          </div>
                        </div>
                        
                        {/* Advanced Feature Separator */}
                        {index < 1 && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            transition={{ delay: 0.6 }}
                            className="hidden lg:flex justify-center mt-24"
                          >
                            <div className="relative w-48 h-px">
                              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#6C63FF]/40 to-transparent" />
                              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-[#6C63FF]/20 rounded-full border border-[#6C63FF]/30" />
                            </div>
                          </motion.div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
            
                {/* Bottom CTA */}
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="mt-20 text-center"
                >
                  <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-white/5 border border-white/10 mb-8">
                    <Sparkles className="w-5 h-5 text-[#6C63FF]" />
                    <span className="text-sm font-medium text-[#6C63FF]">
                      Ready to experience elite-level interview preparation?
                    </span>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleSignup("landing_cta")}
                      className="px-10 py-4 rounded-full bg-white/5 border border-white/10 text-white font-semibold transition-all duration-300 hover:border-[#6C63FF]/30 hover:bg-white/10 hover:shadow-[0_0_40px_rgba(108,99,255,0.15)] flex items-center justify-center gap-3 group relative overflow-hidden"
                    >
                      {/* Platinum sheen effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                      
                      <span className="relative z-10">Start Free Interview</span>
                      <ArrowRight className="relative z-10 group-hover:translate-x-1 transition-transform" size={20} />
                    </motion.button>
                    
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setIsOpen(true)}
                      className="px-10 py-4 rounded-full bg-white/[0.035] border border-white/10 text-white font-semibold transition-all duration-300 hover:bg-white/10 hover:border-white/20 flex items-center justify-center gap-3 group"
                    >
                      <Play className="w-5 h-5" />
                      <span>Watch Platform Tour</span>
                    </motion.button>
                  </div>
                  
                  <p className="text-sm text-[#EDEDED]/50 mt-6">
                    No credit card required • Free plan includes 2 AI interviews/month • Upgrade when you need more practice
                  </p>
                  <SignupTrustHooks compact />
                </motion.div>
              </div>
            </section>
          </div>
        </section>

        {/* Target Audience Visualization Section */}
        <section className="py-10 relative overflow-hidden">
          {/* Background gradient */}
          {/* <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#6C63FF]/5 to-transparent" /> */}
          
          {/* Floating particles effect */}
          {/* <div className="absolute inset-0">
            <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-[#6C63FF] rounded-full animate-pulse opacity-30" />
            <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-[#8B7CFF] rounded-full animate-pulse opacity-20 animation-delay-1000" />
            <div className="absolute bottom-1/4 left-1/3 w-1.5 h-1.5 bg-[#6C63FF] rounded-full animate-pulse opacity-25 animation-delay-500" />
          </div> */}
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative flex flex-col z-10">
            {/* Section Header */}
            <SectionWrapper className="text-center ">
              <motion.div 
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="inline-flex items-center gap-3 mb-6"
              >
                <div className="w-8 h-px bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent" />
                <span className="text-sm font-medium tracking-wider text-[#6C63FF] uppercase">
                  For Every Candidate
                </span>
                <div className="w-8 h-px bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent" />
              </motion.div>
              
              <motion.h2 
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-8"
              >
                <span className="inline-block bg-gradient-to-r from-white via-white to-white/90 bg-clip-text text-transparent">
                  Built for Every
                </span>
                <br />
                <span className="bg-gradient-to-r from-[#6C63FF] to-[#8B7CFF] bg-clip-text text-transparent">
                  Career Journey
                </span>
              </motion.h2>
              
              <motion.p 
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                className="text-xl text-[#EDEDED]/80 max-w-2xl mx-auto leading-relaxed"
              >
                From students and first-time job seekers to senior professionals and executives, our AI adapts to every career stage with precise, personalized interview preparation.
              </motion.p>
            </SectionWrapper>
                  
            {/* Visualization Container */}
            <div className="mt-16">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8">
                {[
                  {
                    id: 1,
                    title: "Job Seekers & Career Switchers",
                    description: "Early to experienced professionals preparing for technical, behavioral, and leadership interviews",
                    icon: UserCheck,
                    delay: 0.05,
                    image: "/assets/images/job_seekers_c9hm4d.png",
                    bar: "from-[#6C63FF] via-[#8B7CFF] to-cyan-400",
                    glow: "rgba(108,99,255,0.22)",
                    stats: ["Confidence", "Clarity", "Realism"]
                  },
                  {
                    id: 2,
                    title: "University Students & Fresh Graduates",
                    description: "Final-year students, new graduates, and internship candidates focusing on entry-level readiness",
                    icon: GraduationCap,
                    delay: 0.1,
                    image: "/assets/images/university.png",
                    bar: "from-emerald-400 via-cyan-400 to-[#6C63FF]",
                    glow: "rgba(34,211,238,0.18)",
                    stats: ["Resume-to-Interview", "Junior Role Prep", "Internship Readiness"]
                  },
                  {
                    id: 3,
                    title: "Company & Role Specific Candidates",
                    description: "Tailored preparation for specific companies and roles with realistic simulations",
                    icon: Target,
                    delay: 0.15,
                    image: "/assets/images/company_xfhbif.png",
                    bar: "from-fuchsia-400 via-[#8B7CFF] to-[#6C63FF]",
                    glow: "rgba(217,70,239,0.16)",
                    stats: ["Role-Specific", "Company-Specific", "Performance Analytics"]
                  },
                  {
                    id: 4,
                    title: "Executive & Senior-Level Candidates",
                    description: "Leadership, management, and executive interview tracks with strategic analysis",
                    icon: Award,
                    delay: 0.2,
                    image: "/assets/images/executive.png",
                    bar: "from-amber-300 via-orange-400 to-fuchsia-400",
                    glow: "rgba(251,191,36,0.16)",
                    stats: ["Leadership", "Strategy", "Presence"]
                  },
                  {
                    id: 5,
                    title: "International & Remote Candidates",
                    description: "Cross-border interviews, remote roles, and global company preparation",
                    icon: Globe,
                    delay: 0.25,
                    image: "/assets/images/international_rvs5mw.png",
                    bar: "from-sky-400 via-[#6C63FF] to-emerald-400",
                    glow: "rgba(56,189,248,0.16)",
                    stats: ["Global", "Remote", "Communication"]
                  },
                  {
                    id: 6,
                    title: "Mentors & Career Coaches",
                    description: "Reviewing performance reports and guiding candidates with AI-generated insights",
                    icon: Users,
                    delay: 0.3,
                    image: "/assets/images/mentors_e9b9hd.png",
                    bar: "from-pink-400 via-fuchsia-400 to-[#6C63FF]",
                    glow: "rgba(236,72,153,0.14)",
                    stats: ["Analytics", "Guidance", "Export"]
                  }
                ].map((persona) => (
                  <motion.div
                    key={persona.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: persona.delay, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <GlassCard className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.035] hover:bg-white/[0.06] transition-colors duration-300">
                      <div className={`h-1.5 w-full bg-gradient-to-r ${persona.bar}`} />
                      <div
                        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                        style={{ background: `radial-gradient(720px 260px at 20% 10%, ${persona.glow}, transparent 55%)` }}
                      />
                      <div className="relative p-6">
                        <div className="flex items-start gap-4">
                          <div className="relative w-14 h-14 rounded-2xl overflow-hidden border border-white/10 bg-black/20 flex-shrink-0">
                            <img
                              src={persona.image}
                              alt={persona.title}
                              loading="lazy"
                              className="absolute inset-0 w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.src = "/assets/images/Gemini_Generated_6_dlmovh.png";
                              }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-black/30 pointer-events-none" />
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-3">
                              <h3 className="text-lg font-semibold text-white leading-tight">
                                {persona.title}
                              </h3>
                              <div className="w-10 h-10 rounded-2xl border border-white/10 bg-white/5 flex items-center justify-center flex-shrink-0">
                                <persona.icon className="w-5 h-5 text-white/85" />
                              </div>
                            </div>
                            <p className="mt-2 text-sm text-white/65 leading-relaxed">
                              {persona.description}
                            </p>
                          </div>
                        </div>

                        <div className="mt-5 flex flex-wrap gap-2">
                          {persona.stats.map((stat) => (
                            <span
                              key={stat}
                              className="px-2.5 py-1 text-xs rounded-xl border border-white/10 bg-white/5 text-white/75"
                            >
                              {stat}
                            </span>
                          ))}
                        </div>
                      </div>
                    </GlassCard>
                  </motion.div>
                ))}
              </div>
            </div>
        
            {/* Bottom CTA */}
            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-center mt-20 md:mt-32"
            >
              <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-white/5 border border-white/10 mb-8">
                <Sparkles className="w-5 h-5 text-[#6C63FF]" />
                <span className="text-sm font-medium text-[#6C63FF]">
                  Your personalized interview journey starts here
                </span>
              </div>
              
              {/* <div className="flex flex-col sm:flex-row gap-4 justify-center px-4">
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleSignup("landing_cta")}
                  className="px-8 py-4 rounded-xl bg-gradient-to-r from-[#6C63FF] to-[#8B7CFF] text-white font-semibold transition-all duration-300 hover:shadow-[0_0_60px_rgba(108,99,255,0.3)] flex items-center justify-center gap-3"
                >
                  <UserPlus className="w-5 h-5" />
                  Create Your Profile
                </motion.button>
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setIsOpen(true)}
                  className="px-8 py-4 rounded-xl bg-white/5 border border-white/10 text-white font-semibold transition-all duration-300 hover:bg-white/10 flex items-center justify-center gap-3"
                >
                  <Play className="w-5 h-5" />
                  See Personalization in Action
                </motion.button>
              </div> */}
            </motion.div>
          </div>
        </section>

        {/* How It Works Section */}
        <section id="how-it-works" className="py-24 relative overflow-hidden">

          

          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/[0.02] to-transparent" />
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionWrapper className="text-center mb-20">
              <div className="inline-flex items-center gap-3 mb-6">
                <motion.div 
                initial={{ opacity: 0, x: -500 ,scale:5}}
                whileInView={{ opacity: 1, x: 0, scale:1 }}
                transition={{ duration: 3, ease: [0.22, 1, 0.36, 1] }}
                className="w-8 h-px bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent" />
                <motion.span 
                initial={{ opacity: 0}}
                whileInView={{ opacity: 1}}
                transition={{ duration: 3 }}
                className="text-sm font-medium tracking-wider text-[#6C63FF] uppercase">
                  How It Works
                </motion.span>
                <motion.div 
                initial={{ opacity: 0, x: 500 ,scale:5}}
                whileInView={{ opacity: 1, x: 0, scale:1 }}
                transition={{ duration: 3, ease: [0.22, 1, 0.36, 1] }}
                className="w-8 h-px bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent" />
              </div>
              
              <motion.h2 
              initial={{ opacity: 0, y: 100 ,scale:5}}
              whileInView={{ opacity: 1, y: 0, scale:1 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                <span className="bg-gradient-to-r from-white via-white/90 to-white bg-clip-text text-transparent">
                  Four steps to become
                </span>
                <br />
                <span className="bg-gradient-to-r from-[#6C63FF] to-[#8B7CFF] bg-clip-text text-transparent">
                  interview ready
                </span>
              </motion.h2>
              
              <motion.p 
              initial={{ y: 200 ,opacity:0}}
              whileInView={{ y: 0 ,opacity:1}}
              transition={{ duration: 3, ease: [0.22, 1, 0.36, 1] }}
              className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
                Choose your role, start an AI interview, get instant feedback, then repeat until your answers are clear, confident, and job-ready.
              </motion.p>
            </SectionWrapper>

            <StaggerContainer className="grid lg:grid-cols-1 gap-8">
              {/* Steps */}
              <div className="relative max-w-6xl mx-auto py-12">
                {/* Center Line */}
                <div className="absolute left-8 lg:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#6C63FF]/0 via-[#6C63FF]/50 to-[#6C63FF]/0 lg:-translate-x-1/2" />
                
                {/* Steps */}
                <div className="space-y-12 lg:space-y-24">
                  {[
                    {
                      number: 1,
                      icon: Target,
                      title: "Choose Your Target Role",
                      description: "Select the job, industry, interview type, difficulty, and focus areas you want to train for.",
                      highlights: ["Role-specific practice", "Behavioral or technical focus", "Company-style scenarios"]
                    },
                    {
                      number: 2,
                      icon: Bot,
                      title: "Start a Realistic AI Interview",
                      description: "Answer voice or chat questions from an AI interviewer that adapts based on your responses.",
                      highlights: ["Follow-up questions", "Pressure simulation", "Panel interview support"]
                    },
                    {
                      number: 3,
                      icon: Lightbulb,
                      title: "Get Instant Feedback",
                      description: "See what worked, what was weak, and what to rewrite before your next attempt.",
                      highlights: ["STAR structure checks", "Clarity and confidence scoring", "Actionable improvements"]
                    },
                    {
                      number: 4,
                      icon: TrendingUp,
                      title: "Improve, Repeat, Win",
                      description: "Track progress until your answers become structured, confident, and ready for real interviews.",
                      highlights: ["Progress analytics", "Badges and streaks", "Interview readiness score"]
                    }
                  ].map((step, index) => {
                    const isEven = index % 2 === 0;
                    
                    return (
                      <StaggerItem key={index} index={index} className={`relative flex flex-col lg:flex-row items-center gap-8 lg:gap-24 ${isEven ? 'lg:flex-row-reverse' : ''}`}>
                        
                        {/* Card Side */}
                        <div className="flex-1 w-full lg:w-auto pl-20 lg:pl-0 relative">
                          <GlassCard className="p-8 relative group hover:-translate-y-2 transition-transform duration-500 border-white/10 overflow-visible">
                            
                            {/* Number Badge */}
                            <div className={`absolute -top-6 ${isEven ? '-left-6' : '-right-6'} w-12 h-12 rounded-2xl bg-[#6C63FF] flex items-center justify-center shadow-[0_0_20px_rgba(108,99,255,0.4)] z-10 hidden lg:flex`}>
                              <span className="text-xl font-bold text-white">{step.number}</span>
                            </div>

                            {/* Mobile Number Badge */}
                            <div className="absolute -left-14 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-[#0B0B12] border border-[#6C63FF] flex items-center justify-center lg:hidden z-10 shadow-[0_0_15px_rgba(108,99,255,0.4)]">
                              <span className="text-sm font-bold text-white">{step.number}</span>
                            </div>
                            
                            <div className="flex flex-col sm:flex-row items-start gap-6">
                              <div className="w-14 h-14 rounded-2xl border border-white/10 bg-white/5 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-500">
                                <step.icon className="w-7 h-7 text-white" />
                              </div>
                              
                              <div className="flex-1">
                                <h3 className="text-xl font-bold text-white mb-3">{step.title}</h3>
                                <p className="text-gray-400 text-sm leading-relaxed mb-6">
                                  {step.description}
                                </p>

                                <div className="space-y-3">
                                  {step.highlights.map((highlight, i) => (
                                    <div key={i} className="flex items-center gap-3">
                                      <div className="w-6 h-6 rounded-full bg-[#6C63FF]/10 flex items-center justify-center flex-shrink-0">
                                        <CheckCircle className="w-3.5 h-3.5 text-[#6C63FF]" />
                                      </div>
                                      <span className="text-sm text-gray-300">{highlight}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </GlassCard>
                          
                          {/* Arrow Connector */}
                          {index < 3 && (
                            <div className={`hidden lg:block absolute -bottom-28 ${isEven ? 'left-8' : 'right-8'} text-[#6C63FF]/30 pointer-events-none z-0`}>
                              <svg width="100" height="120" viewBox="0 0 100 120" fill="none" className={isEven ? "transform -scale-x-100" : ""}>
                                <path d="M 20 0 C 20 60 80 60 80 120" stroke="currentColor" strokeWidth="2" strokeDasharray="6 6" />
                                <path d="M 80 120 L 75 110 M 80 120 L 85 110" stroke="currentColor" strokeWidth="2" />
                              </svg>
                            </div>
                          )}
                        </div>

                        {/* Center Marker */}
                        <div className="absolute left-8 lg:left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-[#0B0B12] border-4 border-[#6C63FF] shadow-[0_0_10px_#6C63FF] z-10" />

                        {/* Spacer Side */}
                        <div className="flex-1 hidden lg:block" />

                      </StaggerItem>
                    );
                  })}
                </div>
              </div>

              {/* AI Features Card */}
              <StaggerItem index={0} className="sticky top-24">
                <GlassCard className="p-10 relative overflow-hidden group">
                  {/* Background Effects */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#6C63FF]/5 via-transparent to-[#8B7CFF]/5 pointer-events-none" />
                  <div className="absolute -top-24 -right-24 w-64 h-64 bg-[#6C63FF]/20 rounded-full blur-[100px] group-hover:bg-[#6C63FF]/30 transition-colors duration-500" />
                  <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-[#8B7CFF]/20 rounded-full blur-[100px] group-hover:bg-[#8B7CFF]/30 transition-colors duration-500" />
                  
                  {/* Header Section */}
                  <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-12 relative z-10">
                    <div className="flex items-center gap-6">
                      <div className="relative">
                        <div className="w-20 h-20 rounded-2xl bg-[#0B0B12] border border-[#6C63FF]/30 flex items-center justify-center shadow-[0_0_30px_rgba(108,99,255,0.2)]">
                          <Cpu className="w-10 h-10 text-[#6C63FF]" />
                        </div>
                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full border-4 border-[#0B0B12] flex items-center justify-center">
                          <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-3xl font-bold text-white mb-2">
                          AI Analysis Engine
                        </h3>
                        <p className="text-gray-400 flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-green-500" />
                          Active Monitoring System
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-md">
                      <Activity className="w-4 h-4 text-[#6C63FF]" />
                      <span className="text-sm font-medium text-white">Real-time Processing</span>
                    </div>
                  </div>

                  {/* Main Features Grid */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 relative z-10">
                    {[
                      { 
                        type: "Text Analysis", 
                        icon: MessageSquare, 
                        metric: "98%", 
                        label: "Accuracy",
                        desc: "NLP-powered response evaluation",
                        color: "from-blue-500 to-indigo-500"
                      },
                      { 
                        type: "Speech Pattern", 
                        icon: Mic, 
                        metric: "95%", 
                        label: "Clarity",
                        desc: "Tone & pacing analysis",
                        color: "from-purple-500 to-pink-500"
                      },
                      { 
                        type: "Visual Cues", 
                        icon: Video, 
                        metric: "92%", 
                        label: "Engagement", 
                        desc: "Body language tracking",
                        color: "from-orange-500 to-red-500",
                        badge: "Coming Soon"
                      }
                    ].map((feature, index) => (
                      <motion.div
                        key={feature.type}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="group/card relative"
                      >
                        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent rounded-3xl blur-xl opacity-0 group-hover/card:opacity-100 transition-opacity duration-500" />
                        
                        <div className="relative h-full bg-[#0B0B12]/40 backdrop-blur-md border border-white/10 rounded-3xl p-6 hover:border-white/20 transition-colors duration-300 overflow-hidden">
                          {/* Hover Gradient */}
                          <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover/card:opacity-5 transition-opacity duration-500`} />
                          
                          {/* Coming Soon Badge */}
                          {feature.badge && (
                            <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-white/5 border border-white/10 backdrop-blur-md">
                              <span className="text-[10px] font-bold text-white uppercase tracking-wider">{feature.badge}</span>
                            </div>
                          )}

                          <div className="flex flex-col h-full justify-between">
                            <div>
                              <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${feature.color} p-[1px] mb-6`}>
                                <div className="w-full h-full rounded-2xl bg-[#0B0B12] flex items-center justify-center">
                                  <feature.icon className="w-6 h-6 text-white" />
                                </div>
                              </div>
                              
                              <h4 className="text-xl font-bold text-white mb-2">{feature.type}</h4>
                              <p className="text-sm text-gray-400 mb-6">{feature.desc}</p>
                            </div>

                            <div className="flex items-end justify-between">
                              <div>
                                <div className="text-3xl font-bold text-white mb-1">{feature.metric}</div>
                                <div className="text-xs text-gray-500 uppercase tracking-wider">{feature.label}</div>
                              </div>
                              
                              {/* Mini Chart Visualization */}
                              <div className="flex items-end gap-1 h-8">
                                {[40, 70, 50, 90, 60].map((h, i) => (
                                  <motion.div
                                    key={i}
                                    initial={{ height: 0 }}
                                    whileInView={{ height: `${h}%` }}
                                    transition={{ delay: 0.5 + (i * 0.1), duration: 1, repeat: Infinity, repeatType: "reverse" }}
                                    className={`w-1.5 rounded-t-sm bg-gradient-to-t ${feature.color} opacity-50`}
                                  />
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Advanced Metrics Visualization */}
                  <div className="mt-8 pt-8 border-t border-white/10 relative z-10">
                    <div className="flex items-center justify-between mb-8">
                      <h4 className="text-lg font-bold text-white">Deep Performance Analytics</h4>
                      <button className="text-sm text-[#6C63FF] hover:text-white transition-colors">View Details →</button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      {[
                        { label: "Confidence Score", value: 85, color: "#6C63FF" },
                        { label: "Vocabulary Richness", value: 92, color: "#10B981" },
                        { label: "Pacing & Tone", value: 78, color: "#F59E0B" },
                        { label: "Emotional EQ", value: 88, color: "#EC4899" }
                      ].map((metric, index) => (
                        <div key={metric.label} className="bg-white/5 rounded-2xl p-4 border border-white/5 hover:bg-white/10 transition-colors">
                          <div className="flex justify-between items-start mb-4">
                            <span className="text-sm text-gray-400 max-w-[80px] leading-tight">{metric.label}</span>
                            <span className="text-lg font-bold text-white">{metric.value}</span>
                          </div>
                          
                          <div className="relative h-2 bg-white/10 rounded-full overflow-hidden">
                            <motion.div 
                              className="absolute top-0 left-0 h-full rounded-full"
                              initial={{ width: 0 }}
                              whileInView={{ width: `${metric.value}%` }}
                              transition={{ duration: 1.5, delay: index * 0.1 }}
                              style={{ backgroundColor: metric.color }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </GlassCard>
              </StaggerItem>
            </StaggerContainer>
            
          </div>
        </section>

        {/* Demo Section */}
        <section id="demo" className="py-20 relative overflow-hidden">

          

          <div className="max-w-5xl mx-auto px-4 sm:px-6 relative z-10">
            <SectionWrapper className="text-center mb-20">
              <div className="inline-flex items-center gap-3 mb-6">
                <motion.div 
                initial={{ opacity: 0, x: -500 ,scale:5}}
                whileInView={{ opacity: 1, x: 0, scale:1 }}
                transition={{ duration: 3, ease: [0.22, 1, 0.36, 1] }}
                className="w-8 h-px bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent" />
                <motion.span 
                initial={{ opacity: 0}}
                whileInView={{ opacity: 1}}
                transition={{ duration: 3 }}
                className="text-sm font-medium tracking-wider text-[#6C63FF] uppercase">
                  Demo
                </motion.span>
                <motion.div 
                initial={{ opacity: 0, x: 500 ,scale:5}}
                whileInView={{ opacity: 1, x: 0, scale:1 }}
                transition={{ duration: 3, ease: [0.22, 1, 0.36, 1] }}
                className="w-8 h-px bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent" />
              </div>
              
              <motion.h2 
              initial={{ opacity: 0, y: 200 ,scale:0}}
              whileInView={{ opacity: 1, y: 0, scale:1 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                <span className="bg-gradient-to-r from-white via-white/90 to-white bg-clip-text text-transparent">
                  Experience AI-Powered 
                </span>
                <br />
                <span className="bg-gradient-to-r from-[#6C63FF] to-[#8B7CFF] bg-clip-text text-transparent">
                  Interview Coaching
                </span>
              </motion.h2>
              
              <motion.p 
              initial={{ y: 200 ,opacity:0}}
              whileInView={{ y: 0 ,opacity:1}}
              transition={{ duration: 3, ease: [0.22, 1, 0.36, 1] }}
              className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
                Try our interactive demo and see how our AI transforms your interview skills
              </motion.p>
            </SectionWrapper>

            
            <GlassCard className="p-8">
              {demoState === 'idle' && <IdleState onStartDemo={handleStartDemo} />}
              {demoState === 'listening' && <ListeningState onRespond={handleRespond} />}
              {demoState === 'analyzing' && <AnalyzingState />}
              {demoState === 'results' && <ResultsState onRetry={handleRetry} />}
            </GlassCard>
        
            {/* Trust Badges */}
            {/* <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="text-center mt-12"
            >
              <p className="text-gray-400 text-sm mb-4">Trusted by candidates from</p>
              <div className="flex justify-center items-center gap-8 flex-wrap">
                {['Google', 'Meta', 'Amazon', 'Microsoft', 'Netflix'].map((company) => (
                  <div key={company} className="flex items-center gap-2 text-gray-300">
                    <div className="w-2 h-2 rounded-full bg-green-400"></div>
                    <span className="text-sm font-medium">{company}</span>
                  </div>
                ))}
              </div>
            </motion.div> */}
          </div>
        </section>

        {/* FAQ Section */}
        <section id="faq" className="py-10 relative overflow-hidden">

        
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/[0.02] to-transparent" />
          
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <SectionWrapper className="text-center mb-20">
              <div className="inline-flex items-center gap-3 mb-6">
                <motion.div 
                initial={{ opacity: 0, x: -500 ,scale:5}}
                whileInView={{ opacity: 1, x: 0, scale:1 }}
                transition={{ duration: 3, ease: [0.22, 1, 0.36, 1] }}
                className="w-8 h-px bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent" />
                <motion.span 
                initial={{ opacity: 0}}
                whileInView={{ opacity: 1}}
                transition={{ duration: 3 }}
                className="text-sm font-medium tracking-wider text-[#6C63FF] uppercase">
                  FAQ
                </motion.span>
                <motion.div 
                initial={{ opacity: 0, x: 500 ,scale:5}}
                whileInView={{ opacity: 1, x: 0, scale:1 }}
                transition={{ duration: 3, ease: [0.22, 1, 0.36, 1] }}
                className="w-8 h-px bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent" />
              </div>
              
              <motion.h2 
              initial={{ opacity: 0, y: 100 ,scale:5}}
              whileInView={{ opacity: 1, y: 0, scale:1 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                <span className=" mr-3 bg-gradient-to-r from-white via-white/90 to-white bg-clip-text text-transparent">
                  Frequently Asked 
                </span>
                {/* <br /> */}
                <span className="bg-gradient-to-r from-[#6C63FF] to-[#8B7CFF] bg-clip-text text-transparent">
                  Questions
                </span>
              </motion.h2>
              
              <motion.p 
              initial={{ y: 200 ,opacity:0}}
              whileInView={{ y: 0 ,opacity:1}}
              transition={{ duration: 3, ease: [0.22, 1, 0.36, 1] }}
              className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
                Everything you need to know about the product
              </motion.p>
            </SectionWrapper>
            

            
            <div className="space-y-4">
              {faqItems.map((item, index) => (
                <StaggerItem key={index} index={index}>
                  <GlassCard className="p-6">
                    <button 
                      onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                      className="flex items-center justify-between w-full text-left"
                    >
                      <h3 className="text-lg font-medium text-white">{item.question}</h3>
                      {expandedFaq === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                    
                    <AnimatePresence>
                      {expandedFaq === index && (
                        <motion.p 
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="mt-4 text-gray-400 overflow-hidden"
                        >
                          {item.answer}
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </GlassCard>
                </StaggerItem>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-32 relative overflow-hidden">
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/[0.02] to-transparent" />
          
          {/* Glow effects */}
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#6C63FF]/5 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#8B7CFF]/5 rounded-full blur-3xl" />
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <SectionWrapper className="text-center mb-20">
              <div className="inline-flex items-center gap-3 mb-6">
                <motion.div 
                  initial={{ opacity: 0, x: -500, scale: 5 }}
                  whileInView={{ opacity: 1, x: 0, scale: 1 }}
                  transition={{ duration: 3, ease: [0.22, 1, 0.36, 1] }}
                  className="w-8 h-px bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent" 
                />
                <motion.span 
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 3 }}
                  className="text-sm font-medium tracking-wider text-[#6C63FF] uppercase"
                >
                  Success Stories
                </motion.span>
                <motion.div 
                  initial={{ opacity: 0, x: 500, scale: 5 }}
                  whileInView={{ opacity: 1, x: 0, scale: 1 }}
                  transition={{ duration: 3, ease: [0.22, 1, 0.36, 1] }}
                  className="w-8 h-px bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent" 
                />
              </div>
              
              <motion.h2 
                initial={{ opacity: 0, y: 100, scale: 5 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewPOrt={{once:true}}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-8"
              >
                <span className="inline-block bg-gradient-to-r from-white via-white to-white/90 bg-clip-text text-transparent">
                  Candidates who
                </span>
                <br />
                <span className="bg-gradient-to-r from-[#6C63FF] to-[#8B7CFF] bg-clip-text text-transparent">
                  train smarter
                </span>
              </motion.h2>
              
              <motion.p 
                initial={{ y: 200 }}
                whileInView={{ y: 0 }}
                transition={{ duration: 2, ease: [0.22, 1, 0.36, 1] }}
                className="text-xl text-[#EDEDED]/80 max-w-3xl mx-auto leading-relaxed"
              >
                Show the transformation: nervous answers become structured, confident responses users can repeat in real interviews
              </motion.p>
            </SectionWrapper>
        
            {/* Animated Testimonials Component */}
            <SectionWrapper className="mb-20">
              <AnimatedTestimonials 
                testimonials={[
                  {
                    quote: "InterviewGenieAI completely transformed my interview approach. The AI feedback on my STAR responses helped me structure my answers perfectly. I went from struggling with behavioral questions to receiving offers from Google, Meta, and Amazon within 6 weeks!",
                    name: "Mohammed Ali",
                    designation: "Senior Product Manager → Google",
                    src: "https://images.unsplash.com/photo-1494790108755-2616b612b786?q=80&w=3387&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
                    stats: [
                      { label: "Score Improvement", value: "65% → 92%" },
                      { label: "Offers", value: "4 in 6 weeks" },
                      { label: "Rating", value: "★★★★★" }
                    ]
                  },
                  {
                    quote: "The technical interview simulations were incredibly realistic. The AI caught subtle issues in my code explanations that I didn't even notice. Landed my dream role at Netflix with a 40% salary increase!",
                    name: "Alex Johnson",
                    designation: "Software Engineer → Netflix",
                    src: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=3387&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
                    stats: [
                      { label: "Technical Score", value: "94%" },
                      { label: "Salary Increase", value: "+40%" },
                      { label: "Rating", value: "★★★★★" }
                    ]
                  },
                  {
                    quote: "As a non-native English speaker, the filler word analysis and pronunciation feedback were game-changers. I gained so much confidence in my communication skills during executive-level interviews.",
                    name: "Maria Rodriguez",
                    designation: "Marketing Director → Apple",
                    src: "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?q=80&w=3560&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
                    stats: [
                      { label: "Filler Words", value: "-78%" },
                      { label: "Confidence", value: "+85%" },
                      { label: "Rating", value: "★★★★★" }
                    ]
                  },
                  {
                    quote: "The behavioral interview preparation was exceptional. The AI helped me articulate my experiences with clarity and impact. Secured a leadership role at Microsoft with improved negotiation power.",
                    name: "David Chen",
                    designation: "Engineering Manager → Microsoft",
                    src: "https://images.unsplash.com/photo-1636041293178-808a6762ab39?q=80&w=3464&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
                    stats: [
                      { label: "Negotiation", value: "+32%" },
                      { label: "Leadership", value: "91%" },
                      { label: "Rating", value: "★★★★★" }
                    ]
                  },
                  {
                    quote: "The video interview analysis transformed my presentation skills. The body language feedback helped me project confidence and authenticity. Received 3 executive offers in the fintech space.",
                    name: "Priya Sharma",
                    designation: "VP of Finance → Stripe",
                    src: "https://images.unsplash.com/photo-1624561172888-ac93c696e10c?q=80&w=2592&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
                    stats: [
                      { label: "Executive Offers", value: "3" },
                      { label: "Body Language", value: "+76%" },
                      { label: "Rating", value: "★★★★★" }
                    ]
                  }
                ]}
              />
            </SectionWrapper>
        
            {/* Stats Row - Premium Platinum Cards */}
            <StaggerContainer className="grid md:grid-cols-4 gap-6 ">
              {[
                { value: "10K+", label: "Candidates Trained", icon: Users, color: "from-[#6C63FF] to-[#8B7CFF]" },
                { value: "94%", label: "Success Rate", icon: TrendingUp, color: "from-[#6C63FF] to-[#8B7CFF]" },
                { value: "4.9/5", label: "Average Rating", icon: Star, color: "from-[#6C63FF] to-[#8B7CFF]" },
                { value: "2.3x", label: "Faster Job Placement", icon: Zap, color: "from-[#6C63FF] to-[#8B7CFF]" }
              ].map((stat, index) => (
                <StaggerItem key={index} index={index}>
                  <div className="relative group">
                    {/* Platinum border glow */}
                    
                    <GlassCard className="p-8 text-center relative border border-white/15 hover:border-white/10 transition-all duration-300 ">
                    <div className="absolute -inset-0.5 bg-gradient-to-br from-white/30 via-white/15 to-white/5 rounded-3xl blur-sm opacity-0 group-hover:opacity-50 transition-opacity duration-500" />
                      {/* Corner accents */}
                      
                      
                      {/* Icon with platinum frame */}
                      <div className="relative w-16 h-16 mx-auto mb-4">
                        <div className="absolute -inset-2 bg-gradient-to-br from-white/[0.02]0 to-transparent rounded-xl blur-md opacity-0 group-hover:opacity-30 transition-opacity duration-300" />
                        <div className="relative w-16 h-16 rounded-2xl border border-white/20 bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-2xl flex items-center justify-center">
                          <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent rounded-2xl" />
                          <stat.icon className="w-8 h-8 text-[#6C63FF] relative z-10" />
                        </div>
                      </div>
                      
                      {/* Stat value with platinum gradient */}
                      <motion.div 
                        initial={{ scale: 0 }}
                        whileInView={{ scale: 1 }}
                        transition={{ duration: 0.5, delay: index * 0.1, type: "spring" }}
                        className="text-4xl font-bold bg-gradient-to-r from-white to-white/90 bg-clip-text text-transparent mb-2"
                      >
                        {stat.value}
                      </motion.div>
                      
                      {/* Label */}
                      <div className="text-sm font-medium text-gray-400 group-hover:text-gray-300 transition-colors">
                        {stat.label}
                      </div>
                      
                      {/* Progress indicator */}
                      <div className="absolute bottom-4 left-4 right-4 h-1 bg-white/5 rounded-full overflow-hidden">
                        <motion.div 
                          className="h-full rounded-full"
                          initial={{ width: 0 }}
                          whileInView={{ width: `${85 + index * 5}%` }}
                          transition={{ duration: 1.5, delay: index * 0.2 }}
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-[#6C63FF] to-[#8B7CFF]" />
                          <div className="absolute top-0 left-0 h-full w-1/3 bg-gradient-to-r from-white/30 via-white/10 to-transparent" />
                        </motion.div>
                      </div>
                    </GlassCard>
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
                </div>
        </section>
        
        {/* Team Section */}
        <section className="pb-10 relative overflow-hidden">
          {/* Background gradients */}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/[0.015] to-transparent" />
          <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] bg-[#6C63FF]/3 rounded-full blur-3xl" />
          <div className="absolute bottom-1/3 right-1/4 w-[500px] h-[500px] bg-[#8B7CFF]/3 rounded-full blur-3xl" />
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            {/* Section Header */}
            <SectionWrapper className="text-center mb-20">
              <motion.div 
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="inline-flex items-center gap-3 mb-6"
              >
                <div className="w-8 h-px bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent" />
                <span className="text-sm font-medium tracking-wider text-[#6C63FF] uppercase">
                  The Minds Behind the Magic
                </span>
                <div className="w-8 h-px bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent" />
              </motion.div>
              
              <motion.h2 
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-8"
              >
                <span className="inline-block bg-gradient-to-r from-white via-white to-white/90 bg-clip-text text-transparent">
                  Meet Our
                </span>
                <br />
                <span className="bg-gradient-to-r from-[#6C63FF] to-[#8B7CFF] bg-clip-text text-transparent">
                  Expert Team
                </span>
              </motion.h2>
              
              <motion.p 
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                className="text-xl text-[#EDEDED]/80 max-w-2xl mx-auto leading-relaxed"
              >
                A passionate team of AI researchers, career coaches, and engineering experts dedicated to revolutionizing interview preparation
              </motion.p>
            </SectionWrapper>
        
            {/* Team Carousel */}
            <SectionWrapper>
              <TeamCarousel 
                members={[
                  {
                    id: "1",
                    name: "Mohammed Dhimni",
                    role: "Founder & CEO",
                    image: "/assets/images/me_vifzki.png",
                    bio: "Founder of InterviewGenieAI, focused on building AI-powered interview preparation tools. Leads product vision, AI strategy, and overall execution of the platform.",
                    expertise: [
                      "AI Product Design",
                      "Interview Systems",
                      "Startup Execution"
                    ],
                    stats: { projects: 10, experience: "3+ years" }
                  },
                  {
                    id: "2",
                    name: "David",
                    role: "Chief Technology Officer",
                    image: "/assets/images/david_p3rjdi.png",
                    bio: "Leads the technical architecture of InterviewGenieAI, focusing on scalable systems, AI integrations, and performance optimization across the platform.",
                    expertise: [
                      "System Architecture",
                      "AI Integration",
                      "Backend Engineering"
                    ],
                    stats: { projects: 15, experience: "6+ years" }
                  },

                  {
                    id: "3",
                    name: "Suhail",
                    role: "Product & UX",
                    image: "/jurica-koletic-7YVZYZeITc8-unsplash_sqqnct.jpg",
                    bio: "Designs intuitive user experiences and product flows, ensuring InterviewGenieAI feels simple, modern, and effective for candidates at every level.",
                    expertise: [
                      "Product Design",
                      "UX/UI",
                      "User Research"
                    ],
                    stats: { projects: 14, experience: "5+ years" }
                  }
                ]
                }
                title="OUR TEAM"
                titleSize="lg"
                autoPlay={4000}
                showArrows={false}
                cardWidth={300}
                // onMemberChange={(member, index) => {
                //   console.log('Active team member:', member.name);
                // }}
              />
            </SectionWrapper>
        
            {/* Team Stats */}
            <StaggerContainer className="grid md:grid-cols-3 gap-8 mt-32">
              {[
                {
                  value: "50+",
                  label: "Years Combined Experience",
                  description: "Our team brings decades of expertise from top tech companies and institutions",
                  icon: Award
                },
                {
                  value: "200+",
                  label: "Successful AI Projects",
                  description: "Proven track record in developing cutting-edge artificial intelligence solutions",
                  icon: Cpu
                },
                {
                  value: "1000+",
                  label: "Career Placements",
                  description: "Collectively helped thousands secure positions at leading global companies",
                  icon: Users
                }
              ].map((stat, index) => (
                <StaggerItem key={index} index={index}>
                  <div className="relative group h-full">
                    {/* Platinum border glow */}
                    <div className="absolute -inset-0.5 bg-gradient-to-br from-white/30 via-white/15 to-white/5 rounded-3xl blur-sm opacity-0 group-hover:opacity-40 transition-opacity duration-500" />
                    
                    <GlassCard className="p-8 relative border border-white/15 hover:border-white/25 transition-all duration-300 h-full">
                      
                      {/* Icon with platinum frame */}
                      <div className="relative w-14 h-14 mb-6">
                        <div className="absolute -inset-2 bg-gradient-to-br from-white/[0.02]0 to-transparent rounded-xl blur-md opacity-0 group-hover:opacity-30 transition-opacity duration-300" />
                        <div className="relative w-14 h-14 rounded-2xl border border-white/20 bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-2xl flex items-center justify-center">
                          <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent rounded-2xl" />
                          <stat.icon className="w-7 h-7 text-[#6C63FF] relative z-10" />
                        </div>
                      </div>
                      
                      {/* Stat value */}
                      <div className="text-5xl font-bold bg-gradient-to-r from-white to-white/90 bg-clip-text text-transparent mb-3">
                        {stat.value}
                      </div>
                      
                      {/* Label */}
                      <h3 className="text-xl font-bold text-white mb-3">
                        {stat.label}
                      </h3>
                      
                      {/* Description */}
                      <p className="text-gray-400 leading-relaxed">
                        {stat.description}
                      </p>
                      
                      {/* Progress indicator */}
                      <div className="absolute bottom-6 left-6 right-6 h-1 bg-white/5 rounded-full overflow-hidden">
                        <motion.div 
                          className="h-full rounded-full"
                          initial={{ width: 0 }}
                          whileInView={{ width: `${70 + index * 15}%` }}
                          transition={{ duration: 1.5, delay: index * 0.2 }}
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-[#6C63FF] to-[#8B7CFF]" />
                          <div className="absolute top-0 left-0 h-full w-1/3 bg-gradient-to-r from-white/30 via-white/10 to-transparent" />
                        </motion.div>
                      </div>
                    </GlassCard>
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
        
            {/* Premium CTA Card */}
            <SectionWrapper>
              <div className="relative group mt-8">
                {/* Platinum outer glow */}
                <div className="absolute -inset-1 bg-gradient-to-br from-white/30 via-[#6C63FF]/20 to-white/10 rounded-3xl blur-xl opacity-0 group-hover:opacity-50 transition-opacity duration-700" />
                
                <motion.div className="rounded-3xl p-12 relative border border-white/20 hover:border-white/30 transition-all duration-500 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-white/[0.03] via-transparent to-white/[0.01]" />
                  
                  {/* Animated background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/[0.03] via-transparent to-white/[0.01]" />
                  <div className="absolute -top-20 -right-20 w-40 h-40 bg-[#6C63FF]/5 rounded-full blur-3xl" />
                  <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-[#8B7CFF]/5 rounded-full blur-3xl" />
                  
                  {/* Corner accents */}
                  <div className="absolute top-0 left-0 w-8 h-px bg-gradient-to-r from-white/50 via-[#6C63FF] to-transparent" />
                  <div className="absolute top-0 left-0 h-8 w-px bg-gradient-to-b from-white/50 via-[#6C63FF] to-transparent" />
                  <div className="absolute bottom-0 right-0 w-8 h-px bg-gradient-to-l from-white/50 via-[#6C63FF] to-transparent" />
                  <div className="absolute bottom-0 right-0 h-8 w-px bg-gradient-to-t from-white/50 via-[#6C63FF] to-transparent" />
                  
                  <div className="relative z-10 text-center">
                    <h3 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-white via-white/90 to-white bg-clip-text text-transparent mb-6">
                      Ready to Pass Your Next Interview?
                    </h3>
                    <p className="text-xl text-[#EDEDED]/80 mb-10 max-w-2xl mx-auto leading-relaxed">
                      Start practicing now, get instant feedback, and build the confidence to answer like a prepared candidate
                    </p>
                    
                    <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                      <motion.button 
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleSignup("landing_cta")}
                        className="relative group/btn px-10 py-5 rounded-xl bg-gradient-to-br from-[#6C63FF] to-[#8B7CFF] text-white font-semibold text-lg transition-all duration-300 hover:shadow-[0_0_60px_rgba(108,99,255,0.5)] flex items-center justify-center gap-3 min-w-[200px]"
                      >
                        {/* Button shine */}
                        <div className="absolute -bottom-1 left-1/4 w-1/2 h-3 bg-white/30 blur-md rounded-full" />
                        
                        {/* Inner glow */}
                        <div className="absolute inset-0 rounded-xl bg-gradient-to-b from-white/[0.02]0 to-transparent opacity-20" />
                        
                        <Zap className="w-5 h-5 group-hover/btn:animate-pulse" />
                        <span>Start Free Interview</span>
                      </motion.button>
                      
                      <motion.button 
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setIsOpen(true)}
                        className="relative group/btn2 px-10 py-5 rounded-xl backdrop-blur-xl bg-white/5 border border-white/15 text-white font-semibold text-lg transition-all duration-300 hover:bg-white/10 hover:border-white/25 flex items-center justify-center gap-3 min-w-[200px]"
                      >
                        {/* Platinum border effect */}
                        <div className="absolute -inset-px rounded-xl bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover/btn2:opacity-100 transition-opacity duration-300" />
                        
                        <Play className="w-5 h-5" />
                        <span>Watch Platform Tour</span>
                      </motion.button>
                    </div>
                    
                    <p className="text-sm text-gray-500 mt-8 font-medium tracking-wider">
                      No credit card required • Free plan includes 2 AI interviews/month • Upgrade when you need more practice
                    </p>
                    <SignupTrustHooks compact />
                  </div>
                </motion.div>
              </div>
              
            </SectionWrapper>
          </div>
          {/* Footer */}
        <footer className="mt-20 py-16 px-4 sm:px-6 lg:px-8 bg-black/30 border-t border-white/10">
          <div className="max-w-7xl mx-auto">
            <StaggerContainer className="grid md:grid-cols-4 gap-8 mb-12">
              <StaggerItem index={0}>
                <div>
                  <div className="flex items-center gap-2 mb-6">
                    <div className="w-10 h-10 flex items-center justify-center">
                      <img src={"/GenieAI_2.png"} className="text-white" />
                    </div>
                    <span className="font-bold text-white">InterviewGenieAI</span>
                  </div>
                  <p className="text-gray-400 text-sm">The most advanced AI interview preparation platform</p>
                </div>
              </StaggerItem>
              
              {[
                { title: "Product", items: ["Features", "How It Works", "Pricing", "Demo"] },
                { title: "Resources", items: ["Blog", "Tutorials", "Documentation", "Support"] },
                { title: "Company", items: ["About", "Careers", "Contact", "Legal"] }
              ].map((section, index) => (
                <StaggerItem key={index} index={index + 1}>
                  <div>
                    <h4 className="font-bold mb-4 text-white">{section.title}</h4>
                    <ul className="space-y-2 text-sm">
                      {section.items.map((item, itemIndex) => (
                        <li key={itemIndex}>
                          <a href="#" className="text-gray-400 hover:text-[#6C63FF] transition-colors">
                            {item}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center"
            >
              <p className="text-gray-400 text-sm">© 2025 InterviewGenieAI. All rights reserved.</p>
              <div className="flex items-center gap-6 mt-4 md:mt-0">
                {["Terms", "Privacy", "Cookies"].map((item, index) => (
                  <a key={index} href="#" className="text-gray-400 hover:text-[#6C63FF] transition-colors text-sm">
                    {item}
                  </a>
                ))}
              </div>
            </motion.div>
          </div>
        </footer>
        </section>

        
        
      </AppBackground>
    </>

  );
};

export default GlassmorphicLandingPage;

